import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Text,
  Stack,
  Button,
} from 'braid-design-system';
import { getAllItemLots } from '../services/itemLotsService';
import { getAllPaymentModes, PaymentModeResponse } from '../services/paymentModeService';
import { getAllPaymentModeDetails, PaymentModeDetailResponse } from '../services/paymentModeDetailService';
import { processInvoice, getRecentInvoices, countRecentInvoices, getInvoiceDetail, RecentInvoiceResponse, InvoiceDetailResponse } from '../services/invoiceService';
import { processSelectiveReturn, processDirectReturn } from '../services/returnService';
import { getRecentOrders, placeOrder, updateOrder, deleteOrder } from '../services/orderService';
import { getAllCustomers, CustomerResponse, createCustomer, updateCustomer, CustomerCreateUpdateRequest } from '../services/customerService';
import { getCartItems, addCartItem, updateCartItem, removeCartItem, clearCartItems } from '../services/cartService';
import CreateUpdateCustomerDrawer, { CustomerFormData, CustomerToEdit } from '../components/Customer/CreateUpdateCustomerDrawer';
import ReceiptPrintModal from '../components/Billing/ReceiptPrintModal';
import QuotationPrintModal from '../components/Billing/QuotationPrintModal';
import SearchItemsModal from '../components/Billing/SearchItemsModal';
import CardPaymentModal from '../components/Billing/CardPaymentModal';
import ReturnItemSelectionModal from '../components/Billing/ReturnItemSelectionModal';
import PriceChangeModal, { PriceChangeEntry } from '../components/Billing/PriceChangeModal';
import { getItemPriceHistory } from '../services/priceChangeService';
import { getAllUsers, UserWithActiveStatusResponse } from '../services/userService';
import { getAllItemGroups, ItemGroupResponse } from '../services/itemGroupService';
import { getSystemSetup, SetupResponse } from '../services/setupService';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthProvider';
import { usePosPrintConfig } from '../hooks/usePosPrintConfig';
import { printPosBill, PosPrintPayload, formatInvoiceDate, type PrintPaperSize } from '../utils/posPrintRenderer';
import { getAllPromotions, PromoHedResponse, selectPromotion, CartItemRequest } from '../services/promotionService';
import PromotionsViewModal from '../components/Promotion/PromotionsViewModal';
import { getStationId } from '../utils/terminalStation';
import '../styles/BillingScreen.css';
import '../styles/PromotionManagement.css';

interface BillingItem {
  id: string;
  cart_id?: number;
  itemCode?: string;
  name: string;
  orgPrice: number;
  basePrice: number;
  price: number;
  discountedPrice?: number;
  discount: number;
  /** Per-unit discount amount; a line carries either `discount` (%) or this, never both. */
  discountAmount?: number;
  quantity: number;
  total: number;
  sih?: number;
  lineType?: 'SALE' | 'RETURN';
  originalQuantity?: number;
  sourceInvoiceNo?: string;
  sourceLineNo?: number;
}

interface PaymentInfo {
  subtotal: number;
  billDiscount: number;
  billDiscountAmount: number;
  promotionDiscount: number;
  promotionDiscountAmount: number;
  totalDue: number;
  paymentReceived: number;
  allocatedAmount: number;
  outstandingAmount: number;
  change: number;
}

interface AvailableItem {
  id: string;
  itemCode?: string;
  name: string;
  category: string;
  orgPrice: number;
  basePrice: number;
  price: number;
  discount: number;
  barcode?: string;
  itemGroup: string;
  sih: number;
  isWeighted: boolean;
}

interface PaymentMethodButton {
  id: string;
  label: string;
  icon: 'cash' | 'card' | 'qr' | 'other';
}

interface AppliedPaymentAllocation {
  paymode: string;
  label: string;
  amount: number;
  isCredit: boolean;
  isLoyalty: boolean;
  details?: string;
}

interface CardPaymentDetails {
  cardTypeCode: string;
  stationNo: string;
  approvalCode: string;
  cardLastDigits: string;
  amount: number;
}

interface CardPaymentDraft {
  cardTypeCode: string;
  stationNo: string;
  approvalCode: string;
  cardNumber: string;
  amount: string;
}

interface OrderSummary {
  id: string;
  number: string;
  amount: number;
  itemCount: number;
  createdAt: string;
  customerCode: string | null;
  salesRepId: string | null;
  items: BillingItem[];
}

interface BillingPartyDetails {
  customerCode: string;
  customerName: string;
  salesRepId: string;
  salesRepName: string;
  cashierId: string;
  cashierName: string;
}

type BillingSidebarPanel = 'orders' | 'member' | 'staff' | 'receipts' | 'returns' | null;

const normalizeCategoryKey = (value: string | number | null | undefined): string =>
  String(value ?? '').trim().toLowerCase();

const clampDiscount = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.min(100, Math.max(0, value));
};

const roundCurrency = (value: number): number => Math.round(value * 100) / 100;

const isCustomerLoyaltyActive = (value: boolean | number | string | null | undefined): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  return Number(value ?? 0) === 1;
};

const calculateLoyaltyPoints = (
  netAmount: number,
  pointScheme: number | null | undefined,
  isLoyaltyCustomer: boolean,
  redeemedAmount = 0,
): number => {
  const safeNetAmount = Number.isFinite(netAmount) ? netAmount : 0;
  const safePointScheme = Number(pointScheme ?? 0);
  const safeRedeemedAmount = Number.isFinite(redeemedAmount) ? redeemedAmount : 0;
  const pointsEligibleAmount = roundCurrency(safeNetAmount - safeRedeemedAmount);

  if (pointsEligibleAmount <= 0 || safePointScheme <= 0 || !isLoyaltyCustomer) {
    return 0;
  }

  return roundCurrency(pointsEligibleAmount / safePointScheme);
};

const isLoyaltyPaymode = (paycode: string, description: string): boolean => {
  const normalizedCode = paycode.trim().toLowerCase();
  const normalizedDescription = description.trim().toLowerCase();

  return normalizedCode === 'lpt' || normalizedDescription.includes('loyalty');
};

const parseAmountInput = (value: string | null | undefined): number => {
  const parsed = Number.parseFloat(value || '');
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return roundCurrency(parsed);
};

const normalizePromoDate = (value: string | null | undefined): string | null => {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed.slice(0, 10) : null;
};

const normalizePromoTime = (value: string | null | undefined, fallback: string): string => {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed.slice(0, 5) : fallback;
};

const getLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocalTimeKey = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const isPromotionValidNow = (promo: PromoHedResponse, now: Date, activeBranchId?: string | null): boolean => {
  if (!promo.promo_active) {
    return false;
  }

  if (promo.promo_status !== 'approved') {
    return false;
  }

  const today = getLocalDateKey(now);
  const currentTime = getLocalTimeKey(now);
  const dateFrom = normalizePromoDate(promo.promo_datefrom);
  const dateTo = normalizePromoDate(promo.promo_dateto);
  const timeFrom = normalizePromoTime(promo.promo_timefrom, '00:00');
  const timeTo = normalizePromoTime(promo.promo_timeto, '23:59');

  if (dateFrom && today < dateFrom) {
    return false;
  }

  if (dateTo && today > dateTo) {
    return false;
  }

  // promo_days is a bitmask: bit0=Sunday, bit1=Monday, ..., bit6=Saturday
  if (promo.promo_days != null) {
    const todayBit = 1 << now.getDay(); // getDay() returns 0=Sun, 1=Mon, ..., 6=Sat
    if (!(promo.promo_days & todayBit)) {
      return false;
    }
  }

  if (currentTime < timeFrom || currentTime > timeTo) {
    return false;
  }

  // Check location criteria if the user has an active branch
  if (activeBranchId && promo.details && promo.details.length > 0) {
    const locationCriteria = promo.details.find(d => d.criteria_type === 'location');
    if (locationCriteria && locationCriteria.criteria_value) {
      const allowedLocations = locationCriteria.criteria_value.split(',').map(v => v.trim().toLowerCase());
      if (!allowedLocations.includes(activeBranchId.trim().toLowerCase())) {
        return false;
      }
    }
  }

  return true;
};

const isWalkInCustomer = (customer: CustomerResponse | null | undefined): boolean => {
  const normalizedName = (customer?.cus_name || '').trim().toLowerCase();
  return normalizedName.includes('walk in') || normalizedName.includes('walk-in');
};

const clampDiscountAmount = (value: number, basePrice: number): number => {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  const safeBasePrice = Number.isFinite(basePrice) ? Math.max(0, basePrice) : 0;
  return roundCurrency(Math.min(value, safeBasePrice));
};

const calculateDiscountedUnitPrice = (basePrice: number, discount: number, discountAmount = 0): number => {
  const safeBasePrice = Number.isFinite(basePrice) ? basePrice : 0;
  const safeDiscountAmount = clampDiscountAmount(discountAmount, safeBasePrice);
  if (safeDiscountAmount > 0) {
    return roundCurrency(safeBasePrice - safeDiscountAmount);
  }
  const safeDiscount = clampDiscount(discount);
  return roundCurrency(safeBasePrice * (1 - safeDiscount / 100));
};

const buildBillingItem = (
  item: Omit<BillingItem, 'price' | 'total'> & Partial<Pick<BillingItem, 'price' | 'total'>>,
  sign: 1 | -1 = 1
): BillingItem => {

  // Positive qty = sale, Negative qty = return (automatic stock in)
  const quantity = Number.isFinite(item.quantity) ? item.quantity : 1;
  const discount = clampDiscount(item.discount || 0);
  const originalPrice = Number.isFinite(item.basePrice) ? item.basePrice : Number(item.price || 0);
  // Amount discounts are not carried on return lines, and never alongside a percentage.
  const discountAmount = sign < 0 || discount > 0 ? 0 : clampDiscountAmount(item.discountAmount || 0, originalPrice);
  const unitPrice = calculateDiscountedUnitPrice(originalPrice, discount, discountAmount);
  const signedQuantity = sign * quantity;

  return {
    ...item,
    discount,
    discountAmount,
    quantity: signedQuantity,
    price: originalPrice,
    discountedPrice: unitPrice,
    total: roundCurrency(unitPrice * signedQuantity),
  };
};

const getPaymentMethodIcon = (paycode: string, description: string): PaymentMethodButton['icon'] => {
  const normalizedCode = paycode.trim().toLowerCase();
  const normalizedDescription = description.trim().toLowerCase();

  if (
    normalizedCode.includes('cash') ||
    normalizedCode === 'csh' ||
    normalizedDescription.includes('cash')
  ) {
    return 'cash';
  }

  if (
    normalizedCode.includes('card') ||
    normalizedCode === 'dbc' ||
    normalizedCode === 'crc' ||
    normalizedDescription.includes('card')
  ) {
    return 'card';
  }

  if (
    normalizedCode.includes('qr') ||
    normalizedCode === 'qrc' ||
    normalizedDescription.includes('qr')
  ) {
    return 'qr';
  }

  return 'other';
};

const isCardPaymentMode = (paycode: string, description: string): boolean =>
  getPaymentMethodIcon(paycode, description) === 'card';

const createEmptyCardPaymentDraft = (): CardPaymentDraft => ({
  cardTypeCode: '',
  stationNo: '',
  approvalCode: '',
  cardNumber: '',
  amount: '',
});

const getCardEndingDigitCount = (value: number | null | undefined): number => {
  const digitCount = Math.trunc(Number(value) || 0);
  return digitCount === 5 ? 5 : 4;
};

const formatCardNumberInput = (value: string, digitCount: number): string =>
  value.replace(/\D/g, '').slice(0, getCardEndingDigitCount(digitCount));

const formatCardPaymentDetails = (details: CardPaymentDetails): string =>
  `${details.cardTypeCode} | Terminal ${details.stationNo || '-'} | ****${details.cardLastDigits}`;

const setupAllowsMinusStock = (setup: SetupResponse | null): boolean => {
  const allowMinus = setup?.setup_allowminus;
  return allowMinus === true || allowMinus === 1 || String(allowMinus).trim() === '1';
};

const renderPaymentMethodIcon = (icon: PaymentMethodButton['icon']) => {
  switch (icon) {
    case 'cash':
      return (
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2.5" y="5" width="19" height="14" rx="2" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M6 9h.01" />
          <path d="M18 15h.01" />
        </svg>
      );
    case 'card':
      return (
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 10h18" />
          <path d="M7 15h4" />
        </svg>
      );
    case 'qr':
      return (
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="6" height="6" rx="1" />
          <rect x="15" y="3" width="6" height="6" rx="1" />
          <rect x="3" y="15" width="6" height="6" rx="1" />
          <path d="M15 15h2" />
          <path d="M19 15h2" />
          <path d="M15 19h6" />
        </svg>
      );
    case 'other':
    default:
      return (
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
      );
  }
};

const renderBillingNavIcon = (icon: 'search' | 'barcode' | 'member' | 'history' | 'promotions') => {
  switch (icon) {
    case 'search':
      return (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case 'barcode':
      return (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 5v14" />
          <path d="M7 5v14" />
          <path d="M11 5v14" />
          <path d="M14 5v14" />
          <path d="M18 5v14" />
          <path d="M20 5v14" />
        </svg>
      );
    case 'member':
      return (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 21a8 8 0 0 0-16 0" />
          <circle cx="12" cy="8" r="4" />
        </svg>
      );
    case 'history':
      return (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'promotions':
      return (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12a8 8 0 0 0-12-7.5" />
          <circle cx="12" cy="12" r="3" />
          <path d="M21 12a8 8 0 0 0 12-7.5" />
          <path d="M15 12h-4" />
          <path d="M12 9v6" />
          <path d="M12 16.5V15" />
          <path d="M10.5 15h3" />
          <path d="M13.5 15h-3" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M12 2v3" />
          <path d="M12 19v3" />
          <path d="M2 12h3" />
          <path d="M19 12h3" />
          <path d="M4.9 4.9l2.1 2.1" />
          <path d="M17 17l2.1 2.1" />
          <path d="M4.9 19.1l2.1-2.1" />
          <path d="M17 7l2.1-2.1" />
        </svg>
      );
    default:
      return null;
  }
};

const renderTrashIcon = (size = 16) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

const renderSearchGlyphIcon = (size = 16) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const renderCloseGlyphIcon = (size = 16) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const BillingScreen: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const [items, setItems] = useState<BillingItem[]>([]);
  const [allAvailableItems, setAllAvailableItems] = useState<AvailableItem[]>([]);
  const weightedItemCodes = useMemo(() => new Set(
    allAvailableItems
      .filter((item) => item.isWeighted)
      .map((item) => (item.itemCode || item.id).trim().toUpperCase()),
  ), [allAvailableItems]);
  const isWeightedItem = (item: BillingItem): boolean =>
    weightedItemCodes.has((item.itemCode || item.id).trim().toUpperCase());
  const [paymentModes, setPaymentModes] = useState<PaymentModeResponse[]>([]);
  const [cardPaymentConfigs, setCardPaymentConfigs] = useState<PaymentModeDetailResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [staffUsers, setStaffUsers] = useState<UserWithActiveStatusResponse[]>([]);
  const [itemGroups, setItemGroups] = useState<ItemGroupResponse[]>([]);
  const [systemSetup, setSystemSetup] = useState<SetupResponse | null>(null);

  const [paymentAllocations, setPaymentAllocations] = useState<Record<string, string>>({ CSH: '' });
  const [cardPaymentDetails, setCardPaymentDetails] = useState<Record<string, CardPaymentDetails>>({});
  const [billDiscount, setBillDiscount] = useState<string>('0');
  const [searchModalOpen, setSearchModalOpen] = useState<boolean>(false);
  const [cardPaymentModalOpen, setCardPaymentModalOpen] = useState<boolean>(false);
  const [cardPaymentDraft, setCardPaymentDraft] = useState<CardPaymentDraft>(createEmptyCardPaymentDraft());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchModalHideCategories, setSearchModalHideCategories] = useState<boolean>(false);
  const [activeSidebarPanel, setActiveSidebarPanel] = useState<BillingSidebarPanel>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerResponse | null>(null);
  const [loadedReturnInvoice, setLoadedReturnInvoice] = useState<InvoiceDetailResponse | null>(null);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [recentInvoices, setRecentInvoices] = useState<RecentInvoiceResponse[]>([]);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState<string>('');
  const [invoiceSearchResults, setInvoiceSearchResults] = useState<RecentInvoiceResponse[]>([]);
  const RECEIPT_DATE_PAGE_SIZE = 10;
  const [receiptFromDate, setReceiptFromDate] = useState<string>('');
  const [receiptToDate, setReceiptToDate] = useState<string>('');
  const [receiptDateResults, setReceiptDateResults] = useState<RecentInvoiceResponse[] | null>(null);
  const [receiptDateTotal, setReceiptDateTotal] = useState<number>(0);
  const [receiptDatePage, setReceiptDatePage] = useState<number>(1);
  const [isReceiptDateSearching, setIsReceiptDateSearching] = useState<boolean>(false);
  const [receiptDateSearchError, setReceiptDateSearchError] = useState<string | null>(null);
  const [editingOrderNo, setEditingOrderNo] = useState<string | null>(null);
  const [barcodeInput, setBarcodeInput] = useState<string>('');
  const [isInvoiceSubmitting, setIsInvoiceSubmitting] = useState<boolean>(false);

  const [showReturnItemSelectionModal, setShowReturnItemSelectionModal] = useState<boolean>(false);
  const [invoiceItemsBeforeSelection, setInvoiceItemsBeforeSelection] = useState<BillingItem[]>([]);
  const [selectedReturnItems, setSelectedReturnItems] = useState<Set<string>>(new Set());
  const [receiptToPrint, setReceiptToPrint] = useState<RecentInvoiceResponse | null>(null);
  const [isReceiptPrintLoading, setIsReceiptPrintLoading] = useState<boolean>(false);
  const [isOrderSubmitting, setIsOrderSubmitting] = useState<boolean>(false);
  const [quotationToPrint, setQuotationToPrint] = useState<{ payload: PosPrintPayload; orderNo: string; customerName: string; amount: number } | null>(null);
  const [isQuotationPrintLoading, setIsQuotationPrintLoading] = useState<boolean>(false);
  const [printPaperSize, setPrintPaperSize] = useState<PrintPaperSize>('POS');
  const [isVatInvoice, setIsVatInvoice] = useState<boolean>(false);

  const [priceChangeModalOpen, setPriceChangeModalOpen] = useState<boolean>(false);
  const [priceChangeItemName, setPriceChangeItemName] = useState<string>('');
  const [priceChangeItemCode, setPriceChangeItemCode] = useState<string>('');
  const [priceChanges, setPriceChanges] = useState<PriceChangeEntry[]>([]);
  const [priceChangesLoading, setPriceChangesLoading] = useState<boolean>(false);
  const [priceChangesError, setPriceChangesError] = useState<string | null>(null);
  const [memberSearchCode, setMemberSearchCode] = useState<string>('');
  const [staffSearchCode, setStaffSearchCode] = useState<string>('');
  const [promotions, setPromotions] = useState<PromoHedResponse[]>([]);
  const [promotionClock, setPromotionClock] = useState<Date>(() => new Date());
  const [showPromotionsModal, setShowPromotionsModal] = useState<boolean>(false);
  const [appliedPromotions, setAppliedPromotions] = useState<PromoHedResponse[]>([]);
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const billingSidebarRef = useRef<HTMLElement | null>(null);
  const initialPaymodeAppliedRef = useRef(false);
  const billingVerticalNavRef = useRef<HTMLDivElement | null>(null);
  const billingContainerRef = useRef<HTMLDivElement | null>(null);
  const amountDisplayRef = useRef<HTMLDivElement | null>(null);
  const invoiceButtonRef = useRef<HTMLButtonElement | null>(null);

  // Size the screen to the space left under the top/menu bars so the whole POS
  // fits the viewport (1366x768 target). The bars' height varies with wrapping,
  // so measure it instead of hard-coding; the CSS reads --billing-top.
  useEffect(() => {
    const container = billingContainerRef.current;
    if (!container) return;
    const updateTop = () => {
      const top = Math.round(container.getBoundingClientRect().top + window.scrollY);
      container.style.setProperty('--billing-top', `${top}px`);
    };
    updateTop();
    const observer = new ResizeObserver(updateTop);
    observer.observe(document.body);
    window.addEventListener('resize', updateTop);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateTop);
    };
  }, []);

  const [billingPartyDetails, setBillingPartyDetails] = useState<BillingPartyDetails>({
    customerCode: '-',
    customerName: '-',
    salesRepId: '-',
    salesRepName: '-',
    cashierId: '-',
    cashierName: '-',
  });

  const [selectedPaymode, setSelectedPaymode] = useState<string>('');
  // True while the auto-selected Cash amount is still the system-filled Net amount
  // (the cashier hasn't typed their own). It then follows the Net amount as the bill changes.
  const [isCashAutoFilled, setIsCashAutoFilled] = useState<boolean>(false);
  const [isCustomerDrawerOpen, setIsCustomerDrawerOpen] = useState<boolean>(false);
  const [isLoyaltyPaymentModalOpen, setIsLoyaltyPaymentModalOpen] = useState<boolean>(false);
  const [loyaltyPaymentDraft, setLoyaltyPaymentDraft] = useState<string>('');
  const [loyaltyPaymentError, setLoyaltyPaymentError] = useState<string>('');
  const selectedPaymentMode = paymentModes.find((mode) => mode.paycode === selectedPaymode) || null;
  const selectedCardPaymentConfig = cardPaymentConfigs.find(
    (config) => config.paycode === cardPaymentDraft.cardTypeCode,
  ) || null;
  const selectedCustomerLoyaltyPoints = roundCurrency(Number(selectedCustomer?.cus_points ?? 0));
  const selectedCustomerCanUseLoyalty = isCustomerLoyaltyActive(selectedCustomer?.cus_loyaltyactive);
  const isCreditPaymode = Boolean(selectedPaymentMode?.payIsCredit);
  const activeBranchId = user?.active_branch_id;
  const currentlyValidPromotions = React.useMemo(
    () => promotions.filter((promo) => isPromotionValidNow(promo, promotionClock, activeBranchId)),
    [promotionClock, promotions, activeBranchId],
  );
  const hasCurrentlyValidPromotions = currentlyValidPromotions.length > 0;
  const activePromotionTypeCodes = React.useMemo(
    () => Array.from(new Set(
      currentlyValidPromotions
        .map((promo) => (promo.promo_type_code || '').trim().toUpperCase())
        .filter(Boolean)
    )),
    [currentlyValidPromotions],
  );
  const hasEnteredPayments = React.useMemo(
    () => Object.entries(paymentAllocations).some(
      ([paymode, amount]) => !(isCashAutoFilled && paymode === 'CSH') && parseAmountInput(amount) > 0,
    )
      || Object.keys(cardPaymentDetails).length > 0,
    [cardPaymentDetails, isCashAutoFilled, paymentAllocations],
  );
  const appliedPayments = Object.entries(paymentAllocations).reduce<AppliedPaymentAllocation[]>((accumulator, [paymode, rawAmount]) => {
    const amount = parseAmountInput(rawAmount);
    if (amount <= 0) {
      return accumulator;
    }

    const paymodeMode = paymentModes.find((paymentMode) => paymentMode.paycode === paymode);
    const details = cardPaymentDetails[paymode];
    const isLoyalty = isLoyaltyPaymode(paymode, paymodeMode?.paytypedesc || paymode);

    accumulator.push({
      paymode,
      label: paymodeMode?.paytypedesc?.trim() || paymode,
      amount,
      isCredit: Boolean(paymodeMode?.payIsCredit),
      isLoyalty,
      ...(details ? { details: formatCardPaymentDetails(details) } : {}),
      ...(isLoyalty ? { details: `${amount.toFixed(2)} points` } : {}),
    });

    return accumulator;
  }, []);
  const loyaltyRedeemedAmount = roundCurrency(
    appliedPayments
      .filter((paymentEntry) => paymentEntry.isLoyalty)
      .reduce((sum, paymentEntry) => sum + paymentEntry.amount, 0)
  );
  const selectedPaymodeAmount = paymentAllocations[selectedPaymode] ?? '';

  const resetPaymentAllocations = (paymode?: string) => {
    // When promotions are active, don't default to Cash paymode
    const defaultPaymode = paymode ?? (hasCurrentlyValidPromotions ? '' : 'CSH');
    setSelectedPaymode(defaultPaymode);
    setPaymentAllocations(defaultPaymode ? { [defaultPaymode]: '' } : {});
    setIsCashAutoFilled(defaultPaymode === 'CSH');
    setCardPaymentDetails({});
    setCardPaymentDraft(createEmptyCardPaymentDraft());
    setCardPaymentModalOpen(false);
  };

  const clearEnteredPaymentsForCartMutation = () => {
    if (!hasEnteredPayments) {
      return;
    }

    setSelectedPaymode('');
    setPaymentAllocations({});
    setIsCashAutoFilled(false);
    setCardPaymentDetails({});
    setCardPaymentDraft(createEmptyCardPaymentDraft());
    setCardPaymentModalOpen(false);
    setIsLoyaltyPaymentModalOpen(false);
    setLoyaltyPaymentDraft('');
    setLoyaltyPaymentError('');
  };

  const refreshCustomers = useCallback(async () => {
    const customerList = await getAllCustomers();
    setCustomers(customerList);
    setSelectedCustomer((currentCustomer) => {
      if (!currentCustomer?.cus_id) {
        return currentCustomer;
      }

      return customerList.find((customer) => customer.cus_id === currentCustomer.cus_id) ?? currentCustomer;
    });
    return customerList;
  }, []);

  useEffect(() => {
    if (!activeSidebarPanel) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const targetNode = event.target as Node | null;
      if (!targetNode) {
        return;
      }

      if (billingSidebarRef.current?.contains(targetNode) || billingVerticalNavRef.current?.contains(targetNode)) {
        return;
      }

      setActiveSidebarPanel(null);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [activeSidebarPanel]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setPromotionClock(new Date());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  // Fetch item lots and supporting data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [itemLotsData, paymentModesData, cardPaymentConfigData, groupsData, setupData, cartData, promotionsData] = await Promise.all([
          getAllItemLots({ excludeRawItems: true }),
          getAllPaymentModes(),
          getAllPaymentModeDetails().catch(() => []),
          getAllItemGroups().catch(() => []),
          getSystemSetup().catch(() => null),
          getCartItems().catch(() => []),
          getAllPromotions().catch(() => []),
        ]);
        setPaymentModes(paymentModesData);
        setCardPaymentConfigs(cardPaymentConfigData);
        setItemGroups(groupsData);
        setSystemSetup(setupData);
        setPromotions(promotionsData);

        const itemsFormatted: AvailableItem[] = itemLotsData
          .map((item) => ({
            id: item.itemlots_code || item.itemlots_barcode || '',
            itemCode: item.itemlots_code || '',
            name: item.itemlots_desc || item.itemlots_code || 'Unknown Item',
            category: item.itemlots_group || item.itemlots_loc || 'All',
            orgPrice: item.itemlots_cost || 0,
            basePrice: item.itemlots_selling || 0,
            price: item.itemlots_selling || 0,
            discount: 0,
            barcode: item.itemlots_barcode || '',
            itemGroup: item.itemlots_group || '',
            sih: item.itemlots_sih || 0,
            isWeighted: item.item_weighted === true,
          }))
          .filter((item) => Boolean(item.id));
        setAllAvailableItems(itemsFormatted);

        // Map cart items
        if (cartData && cartData.length > 0) {
          const mappedCartItems = cartData.map(c => {
            // The cart stores the discounted unit price; recover the undiscounted base price.
            const discountAmount = c.discount_amount || 0;
            const basePrice = discountAmount > 0
              ? roundCurrency(c.unit_price + discountAmount)
              : c.discount > 0 && c.discount < 100
                ? roundCurrency(c.unit_price / (1 - c.discount / 100))
                : c.unit_price;
            return {
              id: c.item_code || String(c.item_id),
              cart_id: c.id,
              itemCode: c.item_code,
              name: c.item_name,
              orgPrice: basePrice,
              basePrice,
              price: basePrice,
              discountedPrice: c.unit_price,
              discount: c.discount,
              discountAmount,
              quantity: c.qty,
              total: c.total,
            };
          });
          setItems(mappedCartItems);
        }

        const [recentOrdersData, customerList, userList, recentInvoicesData] = await Promise.all([
          getRecentOrders(20).catch(() => []),
          getAllCustomers().catch(() => []),
          getAllUsers().catch(() => []),
          getRecentInvoices(10).catch(() => []),
        ]);

        setCustomers(customerList);
        setStaffUsers(userList);
        setRecentInvoices(recentInvoicesData);

        const defaultCustomer = customerList.find(c =>
          (c.cus_name || '').toLowerCase().includes('walk in')
        );
        if (defaultCustomer) {
          setSelectedCustomer(defaultCustomer);
        }

        setOrders(
          recentOrdersData.map((order) => ({
            id: `order-${order.order_no}`,
            number: order.order_no,
            amount: order.amount,
            itemCount: order.item_count,
            createdAt: order.created_at ? new Date(order.created_at).toLocaleString() : '-',
            customerCode: order.member || null,
            salesRepId: order.rep || null,
            items: order.items.map((item) => ({
              id: String(item.item_id || item.item_code || `${order.order_no}-${item.item_name || 'item'}`),
              itemCode: item.item_code || '',
              name: item.item_name || item.item_code || 'Unknown Item',
              orgPrice: item.original_price || item.unit_price,
              basePrice: item.original_price || item.unit_price,
              price: item.unit_price,
              discount: item.discount || 0,
              discountAmount: item.discount_amount || 0,
              quantity: item.quantity,
              total: item.amount,
            })),
          }))
        );

        setBillingPartyDetails({
          customerCode: defaultCustomer?.cus_code || '-',
          customerName: defaultCustomer?.cus_name || '-',
          salesRepId: '-',
          salesRepName: '-',
          cashierId: String(user?.user_id ?? '-'),
          cashierName: user?.user_name?.trim() || '-',
        });
      } catch (err: any) {
        setError(err.message || 'Failed to load billing data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.user_id, user?.user_name]);

  useEffect(() => {
    if (loading || initialPaymodeAppliedRef.current) {
      return;
    }
    initialPaymodeAppliedRef.current = true;
    if (!hasCurrentlyValidPromotions) {
      setSelectedPaymode('CSH');
      setPaymentAllocations({ CSH: '' });
      setIsCashAutoFilled(true);
    }
  }, [loading, hasCurrentlyValidPromotions]);

  const categories = ['All', ...itemGroups.map(g => g.group_name || g.group_code || '').filter(Boolean)];
  const categoryCodeMap = itemGroups.reduce((acc, g) => {
    if (g.group_name && g.group_code) {
      acc[g.group_name.toLowerCase().trim()] = g.group_code.toLowerCase().trim();
    }
    return acc;
  }, {} as Record<string, string>);

  const filteredSearchResults = allAvailableItems.filter((item) => {
    const selectedCategoryKey = normalizeCategoryKey(selectedCategory);
    const itemGroupKey = normalizeCategoryKey(item.itemGroup);
    const mappedCategoryCode = categoryCodeMap[selectedCategoryKey];

    const matchesCategory =
      selectedCategory === 'All' || itemGroupKey === mappedCategoryCode;
    const normalizedSearchTerm = searchTerm.toLowerCase();
    const matchesSearch =
      item.name.toLowerCase().includes(normalizedSearchTerm) ||
      (item.itemCode || '').toLowerCase().includes(normalizedSearchTerm) ||
      (item.barcode || '').toLowerCase().includes(normalizedSearchTerm);
    return matchesCategory && matchesSearch;
  });

  const filteredCustomers = customers
    .filter((customer) =>
      (customer.cus_code || '').toLowerCase().includes(memberSearchCode.trim().toLowerCase()) ||
      (customer.cus_name || '').toLowerCase().includes(memberSearchCode.trim().toLowerCase())
    )
    .slice(0, 15);

  const filteredStaffUsers = staffUsers
    .filter((staffUser) => staffUser.user_role === 'SALES_REP')
    .filter((staffUser) =>
      String(staffUser.user_id ?? '').toLowerCase().includes(staffSearchCode.trim().toLowerCase()) ||
      (staffUser.user_name || '').toLowerCase().includes(staffSearchCode.trim().toLowerCase())
    )
    .slice(0, 15);

  const calculatePayment = (): PaymentInfo => {
    const subtotal = roundCurrency(items.reduce((sum, item) => sum + item.total, 0));
    const rawPromotionDiscount = appliedPromotions.reduce((sum, promo) => {
      if (promo.promo_disper != null && promo.promo_disper > 0) {
        return sum + roundCurrency(subtotal * (clampDiscount(promo.promo_disper) / 100));
      }
      if (promo.promo_disamt != null && promo.promo_disamt > 0) {
        return sum + roundCurrency(promo.promo_disamt);
      }
      return sum;
    }, 0);
    const promotionDiscountAmount = subtotal > 0
      ? roundCurrency(Math.min(subtotal, rawPromotionDiscount))
      : 0;
    const promotionDiscountValue = subtotal > 0
      ? roundCurrency((promotionDiscountAmount / subtotal) * 100)
      : 0;
    const afterPromo = roundCurrency(subtotal - promotionDiscountAmount);
    const billDiscountValue = clampDiscount(parseFloat(billDiscount) || 0);
    const billDiscountAmount = roundCurrency(afterPromo * (billDiscountValue / 100));
    const totalDue = roundCurrency(afterPromo - billDiscountAmount);
    const isReturnFlow = totalDue < 0;
    const allocatedAmount = roundCurrency(appliedPayments.reduce((sum, payment) => sum + payment.amount, 0));
    const received = roundCurrency(
      appliedPayments
        .filter((payment) => !payment.isCredit)
        .reduce((sum, payment) => sum + payment.amount, 0)
    );
    const outstandingAmount = isReturnFlow ? 0 : Math.max(0, roundCurrency(totalDue - allocatedAmount));
    const change = isReturnFlow
      ? 0
      : appliedPayments.length <= 1
        ? Math.max(0, roundCurrency(received - totalDue))
        : 0;

    return {
      subtotal,
      billDiscount: billDiscountValue,
      billDiscountAmount,
      promotionDiscount: promotionDiscountValue,
      promotionDiscountAmount,
      totalDue,
      paymentReceived: received,
      allocatedAmount,
      outstandingAmount,
      change,
    };
  };

  const handleNumpadClick = (value: string) => {
    if (value === 'Enter') {
      focusInvoiceButton();
      return;
    }

    // The first key on the auto-filled Net amount starts a fresh amount instead of editing it.
    const replacesAutoCash = isCashAutoFilled && selectedPaymode === 'CSH';
    if (replacesAutoCash) {
      setIsCashAutoFilled(false);
    }
    const currentAmount = replacesAutoCash ? '' : selectedPaymodeAmount;

    const updateSelectedPaymentAmount = (updater: (previous: string) => string) => {
      setPaymentAllocations((prev) => {
        const nextValue = updater(replacesAutoCash ? '' : (prev[selectedPaymode] ?? ''));
        const paymodeMode = paymentModes.find((mode) => mode.paycode === selectedPaymode);

        if (isLoyaltyPaymode(selectedPaymode, paymodeMode?.paytypedesc || selectedPaymode)) {
          const amount = parseAmountInput(nextValue);
          const maxLoyaltyPaymentAmount = Math.max(
            0,
            roundCurrency(Math.min(selectedCustomerLoyaltyPoints, payment.totalDue))
          );

          if (amount > maxLoyaltyPaymentAmount) {
            toast.showError('Loyalty Points payment cannot exceed the net amount.');
            return {
              ...prev,
              [selectedPaymode]: maxLoyaltyPaymentAmount > 0 ? maxLoyaltyPaymentAmount.toFixed(2) : '',
            };
          }
        }

        return {
          ...prev,
          [selectedPaymode]: nextValue,
        };
      });
    };

    if (value === 'Delete') {
      updateSelectedPaymentAmount((prev) => prev.slice(0, -1));
    } else if (value === 'Cancel' || value === 'Clear') {
      updateSelectedPaymentAmount(() => '');
    } else if (value === '00') {
      updateSelectedPaymentAmount((prev) => prev + '00');
    } else if (value === '.') {
      if (!currentAmount.includes('.')) {
        updateSelectedPaymentAmount((prev) => (prev ? prev + '.' : '0.'));
      }
    } else {
      updateSelectedPaymentAmount((prev) => prev + value);
    }
  };

  const handleRemoveItem = (id: string) => {
    clearEnteredPaymentsForCartMutation();
    const itemToRemove = items.find((i) => i.id === id);
    if (itemToRemove?.cart_id) {
      removeCartItem(itemToRemove.cart_id).catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
    }
    setItems((prev) => {
      const nextItems = prev.filter((item) => item.id !== id);
      if (nextItems.length === 0) {
        setAppliedPromotions([]);
      }
      return nextItems;
    });
  };

  const handleQuantityChange = (id: string, quantity: number) => {
    const targetItem = items.find(i => i.id === id);
    const signedQuantity = Number.isFinite(quantity) ? quantity : 0;
    if (targetItem && !isWeightedItem(targetItem) && !Number.isInteger(signedQuantity)) {
      toast.showError('Non-weighted items require a whole-number quantity.');
      return;
    }
    if (targetItem && targetItem.quantity !== quantity) {
      clearEnteredPaymentsForCartMutation();
    }

    // Preserve the raw value so the spinner can cross 0 into negative values.
    const roundedQuantity = Math.round(signedQuantity * 1000) / 1000;

    let nextQuantity = roundedQuantity;
    if (loadedReturnInvoice && targetItem?.lineType === 'RETURN') {
      // Weighted returns allow 3dp; cap all returns at the invoiced quantity.
      // No lower floor, so typing "0.5" can pass through 0 without being forced up.
      const maxReturnQuantity = targetItem.originalQuantity || Math.abs(targetItem.quantity) || 1;
      nextQuantity = -Math.min(Math.abs(roundedQuantity), maxReturnQuantity);
    }

    const magnitude = Math.abs(nextQuantity);

    if (!setupAllowsMinusStock(systemSetup) && targetItem && targetItem.sih !== undefined && Math.abs(nextQuantity) > targetItem.sih) {
      toast.showError(`Only ${targetItem.sih} in stock.`);
      return;
    }

    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = buildBillingItem({ ...item, quantity: magnitude, lineType: nextQuantity < 0 ? 'RETURN' : 'SALE' }, nextQuantity < 0 ? -1 : 1);
          if (item.cart_id) {
            updateCartItem(item.cart_id, { qty: updated.quantity, unit_price: updated.discountedPrice, discount: updated.discount, discount_amount: updated.discountAmount ?? 0, total: updated.total }).catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
          }
          return updated;
        }
        return item;
      })
    );
  };

  const handleLineDiscountChange = (id: string, discountValue: string) => {
    const nextDiscount = clampDiscount(parseFloat(discountValue) || 0);
    const targetItem = items.find((i) => i.id === id);
    if (nextDiscount > 0 && (targetItem?.discountAmount || 0) > 0) {
      toast.showError('A line can have either a discount % or a discount amount, not both. Clear the amount first.');
      return;
    }

    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = buildBillingItem({ ...item, discount: nextDiscount });
          if (item.cart_id) {
            updateCartItem(item.cart_id, { qty: updated.quantity, unit_price: updated.discountedPrice, discount: updated.discount, discount_amount: updated.discountAmount ?? 0, total: updated.total }).catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
          }
          return updated;
        }
        return item;
      })
    );
  };

  const handleLineDiscountAmountChange = (id: string, amountValue: string) => {
    const targetItem = items.find((i) => i.id === id);
    if (!targetItem) {
      return;
    }

    const parsedAmount = parseFloat(amountValue);
    const requestedAmount = Number.isFinite(parsedAmount) && parsedAmount > 0 ? roundCurrency(parsedAmount) : 0;
    if (requestedAmount > 0) {
      if (targetItem.lineType === 'RETURN' || targetItem.quantity < 0) {
        toast.showError('A discount amount cannot be applied to a return line.');
        return;
      }
      if ((targetItem.discount || 0) > 0) {
        toast.showError('A line can have either a discount % or a discount amount, not both. Clear the % first.');
        return;
      }
      if (requestedAmount > targetItem.basePrice) {
        toast.showError(`Discount amount cannot exceed the unit price (${targetItem.basePrice.toFixed(2)}).`);
        return;
      }
    }

    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = buildBillingItem({ ...item, discountAmount: requestedAmount });
          if (item.cart_id) {
            updateCartItem(item.cart_id, { qty: updated.quantity, unit_price: updated.discountedPrice, discount: updated.discount, discount_amount: updated.discountAmount ?? 0, total: updated.total }).catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
          }
          return updated;
        }
        return item;
      })
    );
  };

  const handleBillDiscountChange = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    if (!sanitizedValue) {
      setBillDiscount('0');
      return;
    }

    const parsedValue = clampDiscount(parseFloat(sanitizedValue) || 0);
    setBillDiscount(String(parsedValue));
  };

  const handleDiscountKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['-', '+', 'e', 'E'].includes(event.key)) {
      event.preventDefault();
    }
  };

  const closeSearchModal = () => {
    setSearchModalOpen(false);
    setSearchTerm('');
    setSelectedCategory('All');
    setSearchModalHideCategories(false);
    setBarcodeInput('');
    focusBarcodeInput();
  };

  // The price history modal opens on its own after an item is added, so hand focus back for the next scan.
  const closePriceChangeModal = () => {
    setPriceChangeModalOpen(false);
    focusBarcodeInput();
  };

  const handleAddItemFromSearch = (item: AvailableItem) => {
    clearEnteredPaymentsForCartMutation();
    const existingItem = items.find((i) => i.id === item.id);
    const allowMinusStock = setupAllowsMinusStock(systemSetup);

    if (!allowMinusStock && item.sih <= 0) {
      toast.showError(`"${item.name}" is out of stock.`);
      return;
    }

    if (existingItem) {
      if (!allowMinusStock && Math.abs(existingItem.quantity) + 1 > item.sih) {
        toast.showError(`Cannot add more. Only ${item.sih} in stock.`);
        return;
      }
      const isReturnLine = existingItem.quantity < 0 || existingItem.lineType === 'RETURN';
      const nextQty = isReturnLine ? -(Math.abs(existingItem.quantity) + 1) : Math.abs(existingItem.quantity) + 1;
      handleQuantityChange(item.id, nextQty);
    } else {
      // New items are sales by default; users can switch to return by entering a negative quantity.
      const newItem: BillingItem = buildBillingItem({
        id: item.id,
        itemCode: item.itemCode,
        name: item.name,
        orgPrice: item.orgPrice,
        basePrice: item.basePrice,
        price: item.price,
        discount: item.discount,
        quantity: 1,
        total: item.price,
        sih: item.sih,
        lineType: 'SALE',
      }, 1);

      setItems((prev) => [...prev, newItem]);

      // Auto-fetch price history for the newly added item
      const itemCodeForHistory = newItem.itemCode || item.itemCode || newItem.id;
      fetchPriceHistory(itemCodeForHistory, newItem.name);

      addCartItem({
        item_id: 0,
        item_code: newItem.itemCode || newItem.id,
        item_name: newItem.name,
        qty: newItem.quantity,
        unit_price: newItem.discountedPrice ?? calculateDiscountedUnitPrice(newItem.basePrice, newItem.discount, newItem.discountAmount),
        discount: newItem.discount,
        discount_amount: newItem.discountAmount ?? 0,
        total: newItem.total
      }).then(added => {
        setItems(prev => prev.map(i => i.id === newItem.id ? { ...i, cart_id: added.id } : i));
      }).catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
    }
  };

  /**
   * Determines barcode type and extracts item code & weight for custom barcodes.
   * - Standard international barcodes: exactly 13 digits → normal item lookup.
   * - Custom weighing scale barcodes: exactly 10 digits → first 5 = item code, last 5 = weight in grams.
   */
  const parseBarcode = (value: string): {
    type: 'standard' | 'custom';
    itemCode: string;
    weightKg: number;
  } | null => {
    const digitsOnly = value.trim().replace(/\D/g, '');

    if (digitsOnly.length === 13) {
      // Standard international barcode (13-digit)
      return {
        type: 'standard',
        itemCode: digitsOnly,
        weightKg: 1,
      };
    }

    if (digitsOnly.length === 10) {
      // Custom weighing scale barcode (10-digit)
      const itemCodePart = digitsOnly.slice(0, 5);
      const weightGramsStr = digitsOnly.slice(5, 10);
      const weightGrams = parseInt(weightGramsStr, 10);
      const weightKg = weightGrams / 1000;

      if (weightKg <= 0) {
        return null;
      }

      return {
        type: 'custom',
        itemCode: itemCodePart,
        weightKg,
      };
    }

    // Neither standard nor custom barcode
    return null;
  };

  const findItemByBarcode = (value: string): AvailableItem | null => {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      return null;
    }

    return (
      allAvailableItems.find((item) => (item.barcode || '').trim().toLowerCase() === normalizedValue) ||
      allAvailableItems.find((item) => (item.itemCode || '').trim().toLowerCase() === normalizedValue) ||
      allAvailableItems.find((item) => item.id.trim().toLowerCase() === normalizedValue) ||
      null
    );
  };

  const openSearchFromBarcodeInput = (searchText: string) => {
    setSearchTerm(searchText);
    setSelectedCategory('All');
    setSearchModalHideCategories(true);
    setSearchModalOpen(true);
  };

  const handleBarcodeSubmit = () => {
    const rawBarcode = barcodeInput.trim();

    if (!rawBarcode) {
      return;
    }

    // Try to parse as standard or custom barcode
    const parsed = parseBarcode(rawBarcode);

    if (parsed) {
      if (parsed.type === 'standard') {
        // Standard 13-digit barcode: find by full barcode
        const matchedItem = findItemByBarcode(rawBarcode);
        if (!matchedItem) {
          // Open search modal with the barcode as search term, no category filter
          openSearchFromBarcodeInput(rawBarcode);
          return;
        }

        handleAddItemFromSearch(matchedItem);
        setBarcodeInput('');
        focusBarcodeInput();
      } else {
        // Custom 10-digit weighing scale barcode
        const matchedItem = allAvailableItems.find(
          (item) => (item.itemCode || '').trim().toLowerCase() === parsed.itemCode.toLowerCase()
        );

        if (!matchedItem) {
          // Open search modal with the barcode as search term, no category filter
          openSearchFromBarcodeInput(rawBarcode);
          return;
        }

        // Check stock if minus stock is not allowed
        const allowMinusStock = setupAllowsMinusStock(systemSetup);
        if (!allowMinusStock && matchedItem.sih <= 0) {
          toast.showError(`"${matchedItem.name}" is out of stock.`);
          setBarcodeInput('');
          return;
        }

        // Calculate price based on weight: unit_price * weight_in_kg
        if (!matchedItem.isWeighted) {
          toast.showError('Scale barcodes require an item marked as weighted.');
          setBarcodeInput('');
          return;
        }
        const weightKg = parsed.weightKg;
        const totalPrice = roundCurrency(matchedItem.basePrice * weightKg);

        // Build a unique id to avoid conflicts with regular items
        const uniqueId = `${matchedItem.id}-wgt-${Date.now()}`;

        const newItem: BillingItem = {
          id: uniqueId,
          itemCode: matchedItem.itemCode,
          name: `${matchedItem.name} (${weightKg.toFixed(3)} kg)`,
          orgPrice: matchedItem.basePrice,
          basePrice: matchedItem.basePrice,
          price: matchedItem.basePrice,
          discountedPrice: matchedItem.basePrice,
          discount: 0,
          quantity: weightKg,
          total: totalPrice,
          sih: matchedItem.sih,
          lineType: 'SALE',
        };

        clearEnteredPaymentsForCartMutation();
        setItems((prev) => [...prev, newItem]);

        // Auto-fetch price history
        const itemCodeForHistory = matchedItem.itemCode || matchedItem.id;
        fetchPriceHistory(itemCodeForHistory, matchedItem.name);

        addCartItem({
          item_id: 0,
          item_code: matchedItem.itemCode || matchedItem.id,
          item_name: `${matchedItem.name} (${weightKg.toFixed(3)} kg)`,
          qty: weightKg,
          unit_price: matchedItem.basePrice,
          discount: 0,
          total: totalPrice,
        }).then((added) => {
          setItems((prev) => prev.map((i) => (i.id === uniqueId ? { ...i, cart_id: added.id } : i)));
        }).catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));

        setBarcodeInput('');
        focusBarcodeInput();
      }
    } else {
      // Fallback: try to look up the raw input as barcode or item code directly
      const fallbackSearch = allAvailableItems.find(
        (item) =>
          (item.barcode || '').trim().toLowerCase() === rawBarcode.toLowerCase() ||
          (item.itemCode || '').trim().toLowerCase() === rawBarcode.toLowerCase()
      );

      if (!fallbackSearch) {
        // Open search modal with the raw input as search term, no category filter
        openSearchFromBarcodeInput(rawBarcode);
        return;
      }

      handleAddItemFromSearch(fallbackSearch);
      setBarcodeInput('');
      focusBarcodeInput();
    }
  };

  const fetchPriceHistory = async (itemCode: string, itemName: string) => {
    if (!itemCode) return;

    setPriceChangesLoading(true);
    setPriceChangesError(null);
    setPriceChanges([]);
    setPriceChangeItemCode(itemCode);
    setPriceChangeItemName(itemName);

    try {
      const result = await getItemPriceHistory(itemCode);
      const priceChanges = result.price_changes || [];
      setPriceChanges(priceChanges);
      // Only open the modal if there are price change records
      if (priceChanges.length > 0) {
        setPriceChangeModalOpen(true);
      }
    } catch (err: any) {
      setPriceChangesError(err?.message || 'Failed to load price history.');
      setPriceChanges([]);
    } finally {
      setPriceChangesLoading(false);
    }
  };

  const handleSelectPriceFromHistory = (selectedPrice: number) => {
    if (!priceChangeItemCode || selectedPrice <= 0) return;

    const repriceItem = (item: BillingItem): BillingItem => buildBillingItem({
      ...item,
      orgPrice: selectedPrice,
      basePrice: selectedPrice,
      price: selectedPrice,
      quantity: Math.abs(item.quantity),
    }, item.quantity < 0 ? -1 : 1);

    setItems((prev) =>
      prev.map((item) => {
        const itemCode = item.itemCode || item.id;
        return itemCode === priceChangeItemCode ? repriceItem(item) : item;
      })
    );

    // Also sync the cart if the item has a cart_id
    const updatedItem = items.find((item) => {
      const itemCode = item.itemCode || item.id;
      return itemCode === priceChangeItemCode && item.cart_id;
    });
    if (updatedItem?.cart_id) {
      const repriced = repriceItem(updatedItem);
      updateCartItem(updatedItem.cart_id, {
        qty: repriced.quantity,
        discount: repriced.discount,
        discount_amount: repriced.discountAmount ?? 0,
        total: repriced.total,
        unit_price: repriced.discountedPrice,
      }).catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
    }
  };

  const isBillingOverlayOpen = searchModalOpen
    || cardPaymentModalOpen
    || showReturnItemSelectionModal
    || showPromotionsModal
    || priceChangeModalOpen
    || Boolean(receiptToPrint)
    || Boolean(quotationToPrint)
    || isLoyaltyPaymentModalOpen
    || isCustomerDrawerOpen;

  // Physical keyboard numpad support for payment amount entry
  useEffect(() => {
    const handlePhysicalNumpadKeyDown = (event: KeyboardEvent) => {
      // Only activate when a payment mode is selected
      if (!selectedPaymode) {
        return;
      }

      // Only activate when the user is NOT focusing on an input or textarea element
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') {
        return;
      }

      const key = event.key;

      // Map physical keyboard keys to numpad actions
      if (key >= '0' && key <= '9') {
        event.preventDefault();
        handleNumpadClick(key);
        return;
      }

      if (key === '.') {
        event.preventDefault();
        handleNumpadClick('.');
        return;
      }

      if (key === 'Backspace') {
        event.preventDefault();
        handleNumpadClick('Delete');
        return;
      }

      // Enter: amount -> Invoice button -> submit invoice
      if (key === 'Enter') {
        event.preventDefault();
        if (isBillingOverlayOpen) {
          return;
        }
        const activeElement = document.activeElement;
        // A focused payment mode button is selected by the function key handler
        if (activeElement?.classList.contains('payment-method-btn')) {
          return;
        }
        if (activeElement === invoiceButtonRef.current) {
          invoiceButtonRef.current?.click();
          return;
        }
        handleNumpadClick('Enter');
        return;
      }

      if (key === 'Escape') {
        event.preventDefault();
        handleNumpadClick('Cancel');
        return;
      }
    };

    document.addEventListener('keydown', handlePhysicalNumpadKeyDown);
    return () => document.removeEventListener('keydown', handlePhysicalNumpadKeyDown);
  }, [selectedPaymode, selectedCustomerLoyaltyPoints, isCashAutoFilled, isBillingOverlayOpen]);

  // Keyboard function key support: F2 (focus current paymode), F5 (click Invoice),
  // Arrow keys (navigate paymodes), Escape (close modals), Enter (confirm action)
  useEffect(() => {
    const handleFunctionKeyDown = (event: KeyboardEvent) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isInputFocused = activeTag === 'input' || activeTag === 'textarea';

      // F2: Focus the currently selected payment mode button
      if (event.key === 'F2') {
        event.preventDefault();
        const activePaymodeBtn = document.querySelector('.payment-method-btn-active') as HTMLButtonElement | null;
        if (activePaymodeBtn) {
          activePaymodeBtn.focus();
        }
        return;
      }

      // F5: Click the Invoice button
      if (event.key === 'F5') {
        event.preventDefault();
        const invoiceBtn = document.querySelector('.billing-payment-action-btn') as HTMLButtonElement | null;
        if (invoiceBtn && !invoiceBtn.disabled) {
          invoiceBtn.click();
        }
        return;
      }

      // Escape: Close any open modal (works even when input is focused)
      if (event.key === 'Escape') {
        if (cardPaymentModalOpen) {
          event.preventDefault();
          setCardPaymentModalOpen(false);
          return;
        }
        if (searchModalOpen) {
          event.preventDefault();
          closeSearchModal();
          return;
        }
        if (showReturnItemSelectionModal) {
          event.preventDefault();
          setShowReturnItemSelectionModal(false);
          setSelectedReturnItems(new Set());
          setInvoiceItemsBeforeSelection([]);
          setLoadedReturnInvoice(null);
          return;
        }
        if (showPromotionsModal) {
          event.preventDefault();
          setShowPromotionsModal(false);
          return;
        }
        if (priceChangeModalOpen) {
          event.preventDefault();
          closePriceChangeModal();
          return;
        }
        if (receiptToPrint) {
          event.preventDefault();
          setReceiptToPrint(null);
          focusBarcodeInput();
          return;
        }
        if (quotationToPrint) {
          event.preventDefault();
          setQuotationToPrint(null);
          focusBarcodeInput();
          return;
        }
        if (isLoyaltyPaymentModalOpen) {
          event.preventDefault();
          setIsLoyaltyPaymentModalOpen(false);
          setLoyaltyPaymentDraft('');
          setLoyaltyPaymentError('');
          return;
        }
        if (isCustomerDrawerOpen) {
          event.preventDefault();
          setIsCustomerDrawerOpen(false);
          return;
        }
        return;
      }

      // Skip the rest if focused on an input/textarea
      if (isInputFocused) {
        return;
      }

      // ArrowLeft/ArrowRight: Navigate between payment mode buttons
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const paymodeBtns = document.querySelectorAll('.payment-method-btn');
        if (paymodeBtns.length === 0) return;

        const activeIdx = Array.from(paymodeBtns).findIndex(
          (btn) => btn.classList.contains('payment-method-btn-active')
        );

        let nextIdx: number;
        if (event.key === 'ArrowLeft') {
          nextIdx = activeIdx <= 0 ? paymodeBtns.length - 1 : activeIdx - 1;
        } else {
          nextIdx = activeIdx >= paymodeBtns.length - 1 ? 0 : activeIdx + 1;
        }

        (paymodeBtns[nextIdx] as HTMLButtonElement).click();
        (paymodeBtns[nextIdx] as HTMLButtonElement).focus();
        return;
      }

      // Enter: When a payment mode button is focused, select it
      if (event.key === 'Enter') {
        const focusedBtn = document.activeElement;
        if (focusedBtn && focusedBtn.classList.contains('payment-method-btn')) {
          event.preventDefault();
          (focusedBtn as HTMLButtonElement).click();
          return;
        }
      }
    };

    document.addEventListener('keydown', handleFunctionKeyDown);
    return () => document.removeEventListener('keydown', handleFunctionKeyDown);
  }, [
    searchModalOpen,
    cardPaymentModalOpen,
    showReturnItemSelectionModal,
    showPromotionsModal,
    priceChangeModalOpen,
    receiptToPrint,
    quotationToPrint,
    isLoyaltyPaymentModalOpen,
    isCustomerDrawerOpen,
  ]);

  const focusBarcodeInput = (selectText = false) => {
    window.requestAnimationFrame(() => {
      barcodeInputRef.current?.focus();
      if (selectText) {
        barcodeInputRef.current?.select();
      }
    });
  };

  // Keyboard checkout: Enter on an empty barcode moves to the amount, then to the Invoice button.
  const focusPaymentAmount = () => {
    window.requestAnimationFrame(() => {
      amountDisplayRef.current?.focus();
    });
  };

  const focusInvoiceButton = () => {
    window.requestAnimationFrame(() => {
      invoiceButtonRef.current?.focus();
    });
  };

  // Auto-focus barcode input on component mount
  useEffect(() => {
    focusBarcodeInput();
  }, []);

  const payment = calculatePayment();
  // Amount already covered by payments other than the auto-filled cash.
  const autoCashAmount = isCashAutoFilled ? parseAmountInput(paymentAllocations.CSH ?? '') : 0;
  const manualAllocatedAmount = Math.max(0, roundCurrency(payment.allocatedAmount - autoCashAmount));

  // Keep the auto-selected Cash amount equal to the Net amount until the cashier enters their own.
  useEffect(() => {
    if (!isCashAutoFilled || selectedPaymode !== 'CSH') {
      return;
    }
    const target = payment.totalDue > 0 ? payment.totalDue.toFixed(2) : '';
    setPaymentAllocations((prev) => ((prev.CSH ?? '') === target ? prev : { ...prev, CSH: target }));
  }, [isCashAutoFilled, selectedPaymode, payment.totalDue]);
  const maxLoyaltyPaymentAmount = Math.max(
    0,
    roundCurrency(Math.min(selectedCustomerLoyaltyPoints, payment.totalDue))
  );
  const loyaltyPoints = calculateLoyaltyPoints(
    payment.totalDue,
    systemSetup?.setup_pointscheme ?? null,
    selectedCustomerCanUseLoyalty,
    loyaltyRedeemedAmount,
  );
  const totalQuantity = Math.round(items.reduce((sum, item) => sum + item.quantity, 0) * 1000) / 1000;
  const printTimestamp = new Date().toLocaleString();

  const buildPromotionCartItems = (): CartItemRequest[] => items.map((item) => ({
    itemCode: item.itemCode || item.id,
    itemGroup: allAvailableItems.find((availableItem) => availableItem.id === item.id || availableItem.itemCode === item.itemCode)?.itemGroup || '',
    qty: item.quantity,
  }));

  const evaluatePromotions = async (
    trigger: 'member_changed' | 'paymode_selected' | 'card_type_selected',
    context?: { paymode?: string | null; cardTypeCode?: string | null; customerGroupId?: number | null },
  ) => {
    if (activePromotionTypeCodes.length === 0 || items.length === 0) {
      setAppliedPromotions([]);
      return;
    }

    try {
      const paymodesWithAmount = Object.entries(paymentAllocations)
        .filter(([, rawAmount]) => parseAmountInput(rawAmount) > 0)
        .map(([paymode]) => paymode);

      const matchedPromotions = await selectPromotion({
        trigger,
        activeTypeCodes: activePromotionTypeCodes,
        paymode: context?.paymode || selectedPaymode || null,
        cardTypeCode: context?.cardTypeCode || null,
        paymodes: [...new Set([...paymodesWithAmount, context?.paymode || selectedPaymode].filter(Boolean) as string[])],
        cartItems: buildPromotionCartItems(),
        subtotal: payment.subtotal,
        customerGroupId: context?.customerGroupId ?? (Number(selectedCustomer?.cus_groupid) || null),
        locationId: activeBranchId,
      });

      setAppliedPromotions(matchedPromotions.map((promo) => ({
        id: promo.promo_id ?? undefined,
        promo_code: promo.promo_code || '',
        promo_name: promo.promo_name || '',
        promo_type_code: promo.promo_type_code || null,
        promo_active: true,
        promo_status: 'approved',
        promo_disper: promo.promo_disper || null,
        promo_disamt: promo.promo_disamt || null,
        promo_free_item: promo.promo_free_item || null,
        promo_free_voucher: promo.promo_free_voucher || null,
        details: promo.details || [],
      })));
    } catch {
      setAppliedPromotions([]);
    }
  };

  const openLoyaltyPaymentModal = (paymodeId: string) => {
    const existingAmount = parseAmountInput(paymentAllocations[paymodeId] ?? '');
    const suggestedAmount = existingAmount > 0
      ? Math.min(existingAmount, maxLoyaltyPaymentAmount)
      : Math.min(Math.max(0, roundCurrency(payment.totalDue - manualAllocatedAmount)), maxLoyaltyPaymentAmount);

    setSelectedPaymode(paymodeId);
    setLoyaltyPaymentError('');
    setLoyaltyPaymentDraft(suggestedAmount > 0 ? suggestedAmount.toFixed(2) : '');
    setIsLoyaltyPaymentModalOpen(true);
  };

  const handleApplyLoyaltyPayment = () => {
    if (!selectedCustomer?.cus_id || !selectedCustomerCanUseLoyalty) {
      setLoyaltyPaymentError('Select a loyalty customer first.');
      return;
    }

    const amount = parseAmountInput(loyaltyPaymentDraft);
    if (amount <= 0) {
      setLoyaltyPaymentError('Enter a valid points amount.');
      return;
    }

    if (amount > selectedCustomerLoyaltyPoints) {
      setLoyaltyPaymentError('Entered points exceed the customer balance.');
      return;
    }

    if (amount > maxLoyaltyPaymentAmount) {
      setLoyaltyPaymentError('Entered points cannot exceed the net amount.');
      return;
    }

    setPaymentAllocations((prev) => ({
      ...prev,
      LPT: amount.toFixed(2),
    }));
    setSelectedPaymode('LPT');
    setIsLoyaltyPaymentModalOpen(false);
    setLoyaltyPaymentError('');
  };

  const openCardPaymentModal = (paymodeId: string) => {
    const selectedMode = paymentModes.find((mode) => mode.paycode === paymodeId);
    const existingDetails = cardPaymentDetails[paymodeId];
    const outstandingAmount = Math.max(0, roundCurrency(payment.totalDue - manualAllocatedAmount));
    const existingAmount = parseAmountInput(paymentAllocations[paymodeId] ?? '');
    const suggestedAmount = existingAmount > 0 ? existingAmount : outstandingAmount;
    const defaultCardTypeCode = existingDetails?.cardTypeCode || cardPaymentConfigs[0]?.paycode || '';

    setSelectedPaymode(paymodeId);
    setCardPaymentDraft({
      cardTypeCode: defaultCardTypeCode,
      stationNo: existingDetails?.stationNo || '',
      approvalCode: existingDetails?.approvalCode || '',
      cardNumber: '',
      amount: suggestedAmount > 0 ? suggestedAmount.toFixed(2) : '',
    });
    setCardPaymentModalOpen(true);

    if (selectedMode?.payIsCredit && isWalkInCustomer(selectedCustomer)) {
      setActiveSidebarPanel('member');
      toast.showError('This payment mode is credit. Please select a customer instead of the walk in customer.');
    }
  };

  const handleCardDraftChange = (field: keyof CardPaymentDraft, value: string) => {
    let nextValue = value;

    if (field === 'cardTypeCode') {
      const selectedConfig = cardPaymentConfigs.find((config) => config.paycode === value);
      setCardPaymentDraft((prev) => ({
        ...prev,
        cardTypeCode: value,
        cardNumber: formatCardNumberInput(prev.cardNumber, selectedConfig?.payDispno ?? 4),
      }));
      evaluatePromotions('card_type_selected', { paymode: selectedPaymode, cardTypeCode: value });
      return;
    }

    if (field === 'stationNo') {
      nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    }

    if (field === 'approvalCode') {
      nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    }

    if (field === 'cardNumber') {
      const selectedConfig = cardPaymentConfigs.find((config) => config.paycode === cardPaymentDraft.cardTypeCode);
      nextValue = formatCardNumberInput(value, selectedConfig?.payDispno ?? 0);
    }

    if (field === 'amount') {
      nextValue = value.replace(/[^0-9.]/g, '');
    }

    setCardPaymentDraft((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  const handleApplyCardPayment = () => {
    const amount = parseAmountInput(cardPaymentDraft.amount);
    const cardTypeCode = cardPaymentDraft.cardTypeCode.trim().toUpperCase();
    const stationNo = cardPaymentDraft.stationNo.trim().toUpperCase();
    const approvalCode = cardPaymentDraft.approvalCode.trim().toUpperCase();
    const selectedConfig = cardPaymentConfigs.find((config) => config.paycode === cardTypeCode);
    const cardDigits = cardPaymentDraft.cardNumber.replace(/\D/g, '');
    const requiredCardDigits = getCardEndingDigitCount(selectedConfig?.payDispno);
    const cardLastDigits = cardDigits.slice(-requiredCardDigits);

    if (!cardTypeCode) {
      toast.showError('Select the card type.');
      return;
    }

    if (!selectedConfig) {
      toast.showError('Selected card type is not configured.');
      return;
    }

    if (cardDigits.length !== requiredCardDigits) {
      toast.showError(`Enter the last ${requiredCardDigits} card digits.`);
      return;
    }

    if (amount <= 0) {
      toast.showError('Enter a valid card amount.');
      return;
    }

    setPaymentAllocations((prev) => ({
      ...prev,
      [selectedPaymode]: amount.toFixed(2),
    }));
    setCardPaymentDetails((prev) => ({
      ...prev,
      [selectedPaymode]: {
        cardTypeCode,
        stationNo,
        approvalCode,
        cardLastDigits,
        amount,
      },
    }));
    setCardPaymentModalOpen(false);
    evaluatePromotions('card_type_selected', { paymode: selectedPaymode, cardTypeCode });
  };

  const handlePlaceOrder = async () => {
    if (items.length === 0 || isOrderSubmitting) {
      return;
    }

    setIsOrderSubmitting(true);

    try {
      const orderItems = items.map((item) => ({ ...item }));
      const orderPayload = {
        items: orderItems.map((item) => ({
          item_id: Number.isFinite(Number(item.id)) ? Number(item.id) : null,
          item_code: item.itemCode || item.id,
          item_name: item.name,
          quantity: item.quantity,
          unit_price: item.discountedPrice ?? calculateDiscountedUnitPrice(item.basePrice, item.discount, item.discountAmount),
          original_price: item.basePrice,
          discount: item.discount,
          discount_amount: item.discountAmount ?? 0,
          amount: item.total,
        })),
        payment_received: payment.paymentReceived,
        member: selectedCustomer?.cus_code ? selectedCustomer.cus_code.trim().slice(0, 10) : null,
        rep: billingPartyDetails.salesRepId && billingPartyDetails.salesRepId !== '-'
          ? billingPartyDetails.salesRepId.trim().slice(0, 10)
          : null,
      };

      const savedOrder = editingOrderNo
        ? await updateOrder(editingOrderNo, orderPayload)
        : await placeOrder(orderPayload);

      const quotationPayload = buildQuotationPrintPayload(savedOrder.order_no);
      const quotationCustomerName = billingPartyDetails.customerName || 'Walk-in Customer';
      const quotationAmount = payment.totalDue;

      const refreshedOrders = await getRecentOrders(20);
      setOrders(
        refreshedOrders.map((order) => ({
          id: `order-${order.order_no}`,
          number: order.order_no,
          amount: order.amount,
          itemCount: order.item_count,
          createdAt: order.created_at ? new Date(order.created_at).toLocaleString() : '-',
          customerCode: order.member || null,
          salesRepId: order.rep || null,
          items: order.items.map((item) => ({
            id: String(item.item_id || item.item_code || `${order.order_no}-${item.item_name || 'item'}`),
            itemCode: item.item_code || '',
            name: item.item_name || item.item_code || 'Unknown Item',
            orgPrice: item.original_price || item.unit_price,
            basePrice: item.original_price || item.unit_price,
            price: item.unit_price,
            discount: item.discount || 0,
            discountAmount: item.discount_amount || 0,
            quantity: item.quantity,
            total: item.amount,
          })),
        }))
      );
      setActiveSidebarPanel('orders');
      setEditingOrderNo(null);
      setItems([]);
      setAppliedPromotions([]);
      clearCartItems().catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
      resetPaymentAllocations();
      setBillDiscount('0');
      toast.showSuccess(`Order ${savedOrder.order_no} ${editingOrderNo ? 'updated' : 'placed'} successfully.`);
      setQuotationToPrint({
        payload: quotationPayload,
        orderNo: savedOrder.order_no,
        customerName: quotationCustomerName,
        amount: quotationAmount,
      });
    } catch (err: any) {
      toast.showError(err?.message || (editingOrderNo ? 'Failed to update order.' : 'Failed to place order.'));
    } finally {
      setIsOrderSubmitting(false);
    }
  };

  const handleInvoice = async () => {
    if (items.length === 0 || isInvoiceSubmitting) {
      return;
    }

    const hasReturnItems = items.some((item) => item.quantity < 0);
    const hasSaleItems = items.some((item) => item.quantity > 0);
    const isReturnOnlyInvoice = hasReturnItems && !hasSaleItems;
    const requiresPaymentCollection = payment.totalDue > 0;
    const normalizedBillDiscount = requiresPaymentCollection ? payment.billDiscount : 0;
    const normalizedBillDiscountAmount = requiresPaymentCollection ? payment.billDiscountAmount : 0;

    const creditPaymentSelected = appliedPayments.some((paymentEntry) => paymentEntry.isCredit);
    const loyaltyPaymentSelected = appliedPayments.some((paymentEntry) => paymentEntry.isLoyalty);
    const loyaltyPaymentAmount = loyaltyRedeemedAmount;

    if (loyaltyPaymentSelected && (!selectedCustomer?.cus_id || !selectedCustomerCanUseLoyalty)) {
      setActiveSidebarPanel('member');
      toast.showError('Loyalty Points redemption requires a loyalty customer.');
      return;
    }

    if (loyaltyPaymentSelected && loyaltyPaymentAmount > selectedCustomerLoyaltyPoints) {
      toast.showError('Loyalty Points payment exceeds the customer balance.');
      return;
    }

    if (loyaltyPaymentSelected && loyaltyPaymentAmount > payment.totalDue) {
      toast.showError('Loyalty Points payment cannot exceed the net amount.');
      return;
    }

    if (requiresPaymentCollection && creditPaymentSelected && (!selectedCustomer?.cus_id || isWalkInCustomer(selectedCustomer))) {
      setActiveSidebarPanel('member');
      toast.showError('Credit payment requires selecting a customer other than the walk in customer.');
      return;
    }

    if (!selectedCustomer?.cus_id) {
      toast.showError('Please select a customer before creating the invoice.');
      return;
    }

    if (requiresPaymentCollection && appliedPayments.length === 0) {
      toast.showError('Please enter at least one payment amount.');
      return;
    }

    const missingCardPayment = appliedPayments.find((paymentEntry) => {
      const mode = paymentModes.find((paymentMode) => paymentMode.paycode === paymentEntry.paymode);
      if (!mode) {
        return false;
      }

      return isCardPaymentMode(paymentEntry.paymode, mode.paytypedesc || paymentEntry.paymode)
        && !cardPaymentDetails[paymentEntry.paymode];
    });

    if (requiresPaymentCollection && missingCardPayment) {
      openCardPaymentModal(missingCardPayment.paymode);
      toast.showError('Complete the card payment popup before invoicing.');
      return;
    }

    const singlePayment = appliedPayments.length === 1 ? appliedPayments[0] : null;
    const singlePaymentMode = singlePayment
      ? paymentModes.find((paymentMode) => paymentMode.paycode === singlePayment.paymode)
      : null;
    const isSingleCashPayment = Boolean(
      singlePayment &&
      !singlePayment.isCredit &&
      getPaymentMethodIcon(singlePayment.paymode, singlePaymentMode?.paytypedesc || singlePayment.paymode) === 'cash'
    );
    const requiresExactAllocation =
      appliedPayments.length > 1 ||
      appliedPayments.some((paymentEntry) => paymentEntry.isCredit) ||
      (singlePayment !== null && !isSingleCashPayment);

    if (requiresPaymentCollection && requiresExactAllocation && payment.allocatedAmount !== payment.totalDue) {
      toast.showError('Split payment amounts must add up exactly to the invoice total.');
      return;
    }

    if (requiresPaymentCollection && isSingleCashPayment && payment.paymentReceived < payment.totalDue) {
      toast.showError('Cash payment must cover the invoice total.');
      return;
    }

    setIsInvoiceSubmitting(true);

    try {
      // Handle selective returns (referenced invoice with item selection)
      if (loadedReturnInvoice) {
        // Return lines come from the original invoice (negative qty); anything else is a
        // new item the customer is buying in the same transaction.
        const returnCartItems = items.filter((item) => item.quantity < 0);
        const newCartItems = items.filter((item) => item.quantity > 0);

        if (returnCartItems.length === 0) {
          toast.showError('Select at least one item to return.');
          setIsInvoiceSubmitting(false);
          return;
        }

        const selectiveReturnItems = returnCartItems.map((item) => ({
          itemCode: item.itemCode || item.id,
          returnQuantity: Math.abs(item.quantity),
          selected: true,  // Only selected items are in the cart
        }));

        const newSaleItems = newCartItems.map((item) => ({
          itemCode: item.itemCode || item.id,
          itemName: item.name,
          quantity: item.quantity,
          unitPrice: item.discountedPrice ?? calculateDiscountedUnitPrice(item.basePrice, item.discount, item.discountAmount),
          originalPrice: item.basePrice,
          discount: item.discount,
          discountAmount: item.discountAmount ?? 0,
          amount: item.total,
        }));

        const result = await processSelectiveReturn({
          originalInvoiceNo: loadedReturnInvoice.invoice_no,
          customerId: selectedCustomer?.cus_id ?? null,
          customerCode: billingPartyDetails.customerCode === '-' ? null : billingPartyDetails.customerCode,
          customerName: billingPartyDetails.customerName === '-' ? null : billingPartyDetails.customerName,
          cashierId: billingPartyDetails.cashierId === '-' ? null : billingPartyDetails.cashierId,
          items: selectiveReturnItems,
          newItems: newSaleItems,
          paymode: requiresPaymentCollection ? selectedPaymode : 'CSH',
          paymentReceived: requiresPaymentCollection ? payment.paymentReceived : 0,
          billDiscountPercentage: normalizedBillDiscount,
          billDiscountAmount: normalizedBillDiscountAmount,
          payments: requiresPaymentCollection
            ? appliedPayments.map((paymentEntry) => ({
              paymode: paymentEntry.paymode,
              amount: paymentEntry.amount,
              cardTypeCode: cardPaymentDetails[paymentEntry.paymode]?.cardTypeCode,
              cardStationNo: cardPaymentDetails[paymentEntry.paymode]?.stationNo,
              cardApprovalCode: cardPaymentDetails[paymentEntry.paymode]?.approvalCode,
              cardLastDigits: cardPaymentDetails[paymentEntry.paymode]?.cardLastDigits,
            }))
            : [],
          promotions: appliedPromotions
            .filter((promo) => promo.id)
            .map((promo) => {
              const promoDiscountAmount = promo.promo_disper != null && promo.promo_disper > 0
                ? roundCurrency(payment.subtotal * (clampDiscount(promo.promo_disper) / 100))
                : roundCurrency(promo.promo_disamt || 0);

              return {
                promoId: Number(promo.id),
                promoCode: promo.promo_code,
                promoName: promo.promo_name,
                promoDisper: promo.promo_disper ?? null,
                promoDisamt: promo.promo_disamt ?? null,
                promoFreeItem: promo.promo_free_item ?? null,
                promoFreeVoucher: promo.promo_free_voucher ?? null,
                promoDiscountAmount,
              };
            }),
        });

        if (result.return_invoice_no) {
          try {
            await printSavedInvoice(result.return_invoice_no);
          } catch {
            handlePrint(result.return_invoice_no);
          }
        }

        setItems([]);
        setAppliedPromotions([]);
        clearCartItems().catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
        setLoadedReturnInvoice(null);
        resetPaymentAllocations();
        setBillDiscount('0');
        const [recentInvoicesData] = await Promise.all([
          getRecentInvoices(10).catch(() => []),
          refreshCustomers().catch(() => []),
        ]);
        setRecentInvoices(recentInvoicesData);

        toast.showSuccess(result.message || `Selective return ${result.return_invoice_no} processed successfully.`);
      } else if (isReturnOnlyInvoice && hasReturnItems) {
        // Direct return (non-referenced, return-only invoice)
        const directReturnItems = items
          .filter((item) => item.quantity < 0)
          .map((item) => ({
            itemCode: item.itemCode || item.id,
            itemName: item.name,
            returnQuantity: Math.abs(item.quantity),
            unitPrice: item.basePrice,
            discount: item.discount,
          }));

        const result = await processDirectReturn({
          customerId: selectedCustomer?.cus_id ?? null,
          customerCode: billingPartyDetails.customerCode === '-' ? null : billingPartyDetails.customerCode,
          customerName: billingPartyDetails.customerName === '-' ? null : billingPartyDetails.customerName,
          cashierId: billingPartyDetails.cashierId === '-' ? null : billingPartyDetails.cashierId,
          items: directReturnItems,
          paymode: requiresPaymentCollection ? selectedPaymode : 'CSH',
        });

        if (result.return_invoice_no) {
          handlePrint(result.return_invoice_no);
        }

        setItems([]);
        setAppliedPromotions([]);
        clearCartItems().catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
        resetPaymentAllocations();
        setBillDiscount('0');
        setIsVatInvoice(false);
        const [recentInvoicesData] = await Promise.all([
          getRecentInvoices(10).catch(() => []),
        ]);
        setRecentInvoices(recentInvoicesData);

        toast.showSuccess(result.message || `Direct return ${result.return_invoice_no} processed successfully.`);
      } else {
        // Regular invoice (sales or mixed sales+returns with original invoice)
        const result = await processInvoice({
          customerId: selectedCustomer?.cus_id ?? null,
          customerCode: billingPartyDetails.customerCode === '-' ? null : billingPartyDetails.customerCode,
          customerName: billingPartyDetails.customerName === '-' ? null : billingPartyDetails.customerName,
          salesRepId: billingPartyDetails.salesRepId === '-' ? null : billingPartyDetails.salesRepId,
          cashierId: billingPartyDetails.cashierId === '-' ? null : billingPartyDetails.cashierId,
          invoiceDate: new Date().toISOString(),
          paymentReceived: requiresPaymentCollection ? payment.paymentReceived : 0,
          billDiscountPercentage: normalizedBillDiscount,
          billDiscountAmount: normalizedBillDiscountAmount,
          items: items.map((item) => ({
            itemCode: item.itemCode || item.id,
            name: item.name,
            productId: Number.isFinite(Number(item.id)) ? Number(item.id) : null,
            unitPrice: item.discountedPrice ?? calculateDiscountedUnitPrice(item.basePrice, item.discount, item.discountAmount),
            originalPrice: item.basePrice,
            discount: item.discount,
            discountAmount: item.discountAmount ?? 0,
            qty: item.quantity,
            amount: item.total,
          })),
          paymode: requiresPaymentCollection ? selectedPaymode : 'CSH',
          payments: requiresPaymentCollection
            ? appliedPayments.map((paymentEntry) => ({
              paymode: paymentEntry.paymode,
              amount: paymentEntry.amount,
              cardTypeCode: cardPaymentDetails[paymentEntry.paymode]?.cardTypeCode,
              cardStationNo: cardPaymentDetails[paymentEntry.paymode]?.stationNo,
              cardApprovalCode: cardPaymentDetails[paymentEntry.paymode]?.approvalCode,
              cardLastDigits: cardPaymentDetails[paymentEntry.paymode]?.cardLastDigits,
            }))
            : [],
          promotions: appliedPromotions
            .filter((promo) => promo.id)
            .map((promo) => {
              const promoDiscountAmount = promo.promo_disper != null && promo.promo_disper > 0
                ? roundCurrency(payment.subtotal * (clampDiscount(promo.promo_disper) / 100))
                : roundCurrency(promo.promo_disamt || 0);

              return {
                promoId: Number(promo.id),
                promoCode: promo.promo_code,
                promoName: promo.promo_name,
                promoDisper: promo.promo_disper ?? null,
                promoDisamt: promo.promo_disamt ?? null,
                promoFreeItem: promo.promo_free_item ?? null,
                promoFreeVoucher: promo.promo_free_voucher ?? null,
                promoDiscountAmount,
              };
            }),
          note: isReturnOnlyInvoice
            ? `POS return invoice with ${Math.abs(totalQuantity)} item(s)`
            : `POS billing invoice with ${totalQuantity} item(s)`,
          taxPer: isVatInvoice ? Number(systemSetup?.setup_tax || 0) : null,
        });

        const [recentInvoicesData] = await Promise.all([
          getRecentInvoices(10).catch(() => []),
          refreshCustomers().catch(() => []),
        ]);
        setRecentInvoices(recentInvoicesData);

        if (result.invoice_no) {
          try {
            await printSavedInvoice(result.invoice_no);
          } catch {
            handlePrint(result.invoice_no);
          }
        }

        setItems([]);
        setAppliedPromotions([]);
        clearCartItems().catch((err: any) => toast.showError(err?.message || 'Failed to sync cart'));
        resetPaymentAllocations();
        setBillDiscount('0');
        setIsVatInvoice(false);

        toast.showSuccess(result.message || `Invoice ${result.invoice_no} submitted successfully.`);
      }
    } catch (err: any) {
      toast.showError(err?.message || 'Failed to submit invoice.');
    } finally {
      setIsInvoiceSubmitting(false);
      // Ready for the next sale even when nothing was printed; a print restores focus again once its dialog closes.
      focusBarcodeInput();
    }
  };

  const handleLoadOrder = (order: OrderSummary) => {
    setItems(order.items.map((item) => buildBillingItem({ ...item })));
    setAppliedPromotions([]);
    resetPaymentAllocations();
    setBillDiscount('0');
    setLoadedReturnInvoice(null);
    setEditingOrderNo(order.number);

    if (order.customerCode) {
      const matchedCustomer = customers.find(
        (c) => (c.cus_code || '').trim() === order.customerCode,
      );
      if (matchedCustomer) {
        handleSelectCustomer(matchedCustomer);
      }
    }

    if (order.salesRepId) {
      const matchedStaff = staffUsers.find(
        (u) => String(u.user_id ?? '') === order.salesRepId,
      );
      if (matchedStaff) {
        handleSelectStaff(matchedStaff);
      }
    }
  };

  const handleEditOrder = (order: OrderSummary) => {
    handleLoadOrder(order);
    setActiveSidebarPanel(null);
    focusBarcodeInput();
  };

  const handleDeleteOrder = async (order: OrderSummary) => {
    const confirmed = window.confirm(`Delete order #${order.number}?`);

    if (!confirmed) {
      return;
    }

    try {
      await deleteOrder(order.number);
      setOrders((prev) => prev.filter((savedOrder) => savedOrder.id !== order.id));
      toast.showSuccess(`Order #${order.number} deleted successfully.`);
    } catch (err: any) {
      toast.showError(err?.message || 'Failed to delete order.');
    }
  };

  const handleClearCart = () => {
    setItems([]);
    setAppliedPromotions([]);
    resetPaymentAllocations();
    setBillDiscount('0');
    setEditingOrderNo(null);
    setLoadedReturnInvoice(null);
  };

  const handleSearchInvoices = async (searchInput: string) => {
    setInvoiceSearchTerm(searchInput);

    if (!searchInput.trim()) {
      setInvoiceSearchResults([]);
      return;
    }

    // Search across all invoices via backend API (not just cached 10)
    try {
      const results = await getRecentInvoices(50, searchInput.trim());
      setInvoiceSearchResults(results);
    } catch {
      // Fallback to client-side filtering on cached invoices if API fails
      const searchLower = searchInput.toLowerCase().trim();
      const filtered = recentInvoices.filter((invoice) =>
        invoice.invoice_no.toLowerCase().includes(searchLower) ||
        (invoice.customer_name && invoice.customer_name.toLowerCase().includes(searchLower))
      );
      setInvoiceSearchResults(filtered);
    }
  };

  const fetchReceiptDatePage = async (page: number) => {
    setReceiptDateSearchError(null);
    setIsReceiptDateSearching(true);
    try {
      const [total, results] = await Promise.all([
        countRecentInvoices(undefined, receiptFromDate || undefined, receiptToDate || undefined),
        getRecentInvoices(
          RECEIPT_DATE_PAGE_SIZE,
          undefined,
          receiptFromDate || undefined,
          receiptToDate || undefined,
          (page - 1) * RECEIPT_DATE_PAGE_SIZE,
        ),
      ]);
      setReceiptDateTotal(total);
      setReceiptDateResults(results);
      setReceiptDatePage(page);
    } catch (err: any) {
      setReceiptDateSearchError(err?.message || 'Failed to fetch receipts for the selected date range.');
    } finally {
      setIsReceiptDateSearching(false);
    }
  };

  const handleReceiptDateSearch = async () => {
    if (!receiptFromDate && !receiptToDate) {
      setReceiptDateResults(null);
      setReceiptDateTotal(0);
      setReceiptDateSearchError(null);
      return;
    }

    if (receiptFromDate && receiptToDate && receiptFromDate > receiptToDate) {
      setReceiptDateSearchError('Start date must be before the end date.');
      return;
    }

    await fetchReceiptDatePage(1);
  };

  const handleReceiptDatePageChange = (page: number) => {
    if (page < 1 || page > Math.max(1, Math.ceil(receiptDateTotal / RECEIPT_DATE_PAGE_SIZE))) {
      return;
    }
    fetchReceiptDatePage(page);
  };

  const handleClearReceiptDateSearch = () => {
    setReceiptFromDate('');
    setReceiptToDate('');
    setReceiptDateResults(null);
    setReceiptDateTotal(0);
    setReceiptDatePage(1);
    setReceiptDateSearchError(null);
  };

  const handleSelectInvoiceForReturn = async (invoice: RecentInvoiceResponse) => {
    try {
      const detail = await getInvoiceDetail(invoice.invoice_no);

      // Build items but don't auto-add yet
      const returnItems = detail.items.map((detailItem, index) => buildBillingItem({
        id: `${detail.invoice_no}-${detailItem.item_code || index}`,
        itemCode: detailItem.item_code || undefined,
        name: detailItem.item_name || detailItem.item_code || '-',
        orgPrice: detailItem.original_price ?? detailItem.unit_price,
        basePrice: detailItem.original_price ?? detailItem.unit_price,
        price: detailItem.unit_price,
        discount: detailItem.discount ?? 0,
        quantity: Math.abs(detailItem.quantity || 1),  // Keep positive for selection
        total: detailItem.amount,
        originalQuantity: Math.abs(detailItem.quantity || 1),
        sourceInvoiceNo: detail.invoice_no,
        sourceLineNo: index + 1,
        lineType: detailItem.quantity < 0 ? 'RETURN' : 'SALE',
      }, 1));  // Use sign=1 for now (not negative yet)

      // Store customer details
      setBillingPartyDetails((prev) => ({
        ...prev,
        customerCode: detail.customer_code || '-',
        customerName: detail.customer_name || '-',
        cashierId: detail.cashier_id || prev.cashierId,
      }));

      // Store invoice and items, then show selection modal
      setLoadedReturnInvoice(detail);
      setInvoiceItemsBeforeSelection(returnItems);
      setSelectedReturnItems(new Set()); // Start with no items selected
      setShowReturnItemSelectionModal(true);  // Return Item Selection Modal
      setActiveSidebarPanel(null);

      toast.showSuccess(`Invoice ${invoice.invoice_no} ready for item selection.`);
    } catch (err: any) {
      toast.showError(err?.message || 'Failed to load invoice for return.');
    }
  };

  // Handle confirmation of return item selection
  const handleReturnItemSelectionConfirm = () => {
    if (selectedReturnItems.size === 0) {
      toast.showError('Please select at least one item to return.');
      return;
    }

    // Build return items with only selected items
    const returnItems = invoiceItemsBeforeSelection
      .filter((item) => item.lineType !== 'RETURN' && selectedReturnItems.has(item.itemCode || item.id))
      .map((item) => buildBillingItem(item, -1));  // Convert to negative qty

    setItems(returnItems);
    setAppliedPromotions([]);
    setBillDiscount('0');
    resetPaymentAllocations();
    setShowReturnItemSelectionModal(false);
    setActiveSidebarPanel(null);

    toast.showSuccess(`Added ${returnItems.length} item(s) to return.`);
  };

  const handleScanBarcode = () => {
    setSearchModalOpen(false);
    setActiveSidebarPanel(null);
    focusBarcodeInput();
  };

  const toggleSidebarPanel = (panel: Exclude<BillingSidebarPanel, null>) => {
    if (panel === 'member') {
      setMemberSearchCode('');
    }

    if (panel === 'staff') {
      setStaffSearchCode('');
    }

    setActiveSidebarPanel((prev) => (prev === panel ? null : panel));
  };

  const handleSelectCustomer = (customer: CustomerResponse) => {
    setSelectedCustomer(customer);
    setBillingPartyDetails((prev) => ({
      ...prev,
      customerCode: customer.cus_code?.trim() || '-',
      customerName: customer.cus_name?.trim() || '-',
    }));

    if (!isCustomerLoyaltyActive(customer.cus_loyaltyactive)) {
      setPaymentAllocations((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, 'LPT')) {
          return prev;
        }

        const nextAllocations = { ...prev };
        delete nextAllocations.LPT;
        return Object.keys(nextAllocations).length > 0 ? nextAllocations : { CSH: '' };
      });
    }

    setMemberSearchCode('');
    setActiveSidebarPanel(null);
    evaluatePromotions('member_changed', { customerGroupId: Number(customer.cus_groupid) || null });
  };

  const handlePromotionSelect = () => {
    setShowPromotionsModal(false);
  };

  const handlePromotionsModalClose = () => {
    setShowPromotionsModal(false);
  };

  const handleSelectPaymode = async (paymodeId: string) => {
    const paymodeMode = paymentModes.find((mode) => mode.paycode === paymodeId);

    // Another pay mode takes over: drop the untouched auto-filled cash so it is offered the full Net amount.
    if (isCashAutoFilled && paymodeId !== 'CSH') {
      if (isLoyaltyPaymode(paymodeId, paymodeMode?.paytypedesc || paymodeId)
        && (!selectedCustomer?.cus_id || !selectedCustomerCanUseLoyalty)) {
        toast.showError('Select a loyalty customer first.');
        return;
      }
      setIsCashAutoFilled(false);
      setPaymentAllocations((prev) => {
        const nextAllocations = { ...prev };
        delete nextAllocations.CSH;
        return nextAllocations;
      });
    }

    if (isLoyaltyPaymode(paymodeId, paymodeMode?.paytypedesc || paymodeId)) {
      if (!selectedCustomer?.cus_id || !selectedCustomerCanUseLoyalty) {
        toast.showError('Select a loyalty customer first.');
        return;
      }

      openLoyaltyPaymentModal(paymodeId);
      return;
    }

    setSelectedPaymode(paymodeId);

    setPaymentAllocations((prev) => {
      if (Object.prototype.hasOwnProperty.call(prev, paymodeId)) {
        return prev;
      }

      const defaultAmount = paymodeMode?.payIsCredit
        ? String(Math.max(0, roundCurrency(payment.totalDue - manualAllocatedAmount)))
        : '';

      return {
        ...prev,
        [paymodeId]: defaultAmount === '0' ? '' : defaultAmount,
      };
    });

    if (paymodeMode && isCardPaymentMode(paymodeMode.paycode, paymodeMode.paytypedesc || paymodeMode.paycode)) {
      openCardPaymentModal(paymodeId);
      return;
    }

    evaluatePromotions('paymode_selected', { paymode: paymodeId });

    if (paymodeMode?.payIsCredit && isWalkInCustomer(selectedCustomer)) {
      setActiveSidebarPanel('member');
      toast.showError('This payment mode is credit. Please select a customer instead of the walk in customer.');
    }
  };

  const handleSelectStaff = (staffUser: UserWithActiveStatusResponse) => {
    setBillingPartyDetails((prev) => ({
      ...prev,
      salesRepId: String(staffUser.user_id ?? '-'),
      salesRepName: staffUser.user_name?.trim() || '-',
    }));
    setStaffSearchCode('');
    setActiveSidebarPanel(null);
  };

  const handleSaveCustomer = async (formData: CustomerFormData, editingCustomer?: CustomerToEdit) => {
    const toRequestPayload = (data: CustomerFormData): CustomerCreateUpdateRequest => ({
      cus_code: data.cus_code,
      cus_name: data.cus_name,
      cus_title: data.cus_title,
      cus_groupid: data.cus_groupid,
      cus_active: data.cus_active,
      cus_add1: data.cus_add1,
      cus_add2: data.cus_add2,
      cus_add3: data.cus_add3,
      cus_add4: data.cus_add4,
      cus_tep1: data.cus_tep1,
      cus_tep2: data.cus_tep2,
      cus_email: data.cus_email,
      cus_dob: data.cus_dob || null,
      cus_prof: data.cus_prof,
      cus_spouse: data.cus_spouse,
      cus_creditlimit: data.cus_creditlimit,
      cus_creditperiod: data.cus_creditperiod,
      cus_opbal: data.cus_opbal,
      cus_purchase: data.cus_purchase,
      cus_returns: data.cus_returns,
      cus_creditnote: data.cus_creditnote,
      cus_debitnote: data.cus_debitnote,
      cus_payment: data.cus_payment,
      cus_balance: data.cus_balance,
      cus_loyaltyactive: data.cus_loyaltyactive,
      cus_openpoint: data.cus_openpoint,
      cus_earnepoint: data.cus_earnepoint,
      cus_redeempoint: data.cus_redeempoint,
      cus_points: data.cus_points,
    });

    const payload = toRequestPayload(formData);

    if (editingCustomer?.cus_id) {
      await updateCustomer(editingCustomer.cus_id, payload);
    } else {
      await createCustomer(payload);
    }
  };

  const handleCustomerSaved = async () => {
    try {
      await refreshCustomers();
    } catch (err: any) {
      toast.showError(`Error refreshing customers: ${err.message}`);
    }
  };

  const buildPosPrintPayloadFromInvoiceDetail = (detail: InvoiceDetailResponse): PosPrintPayload => {
    const createdAt = detail.created_at ? new Date(detail.created_at) : null;
    const hasReturnItems = (detail.items || []).some((item: any) => Number(item.quantity ?? item.qty ?? 0) < 0);
    const saleItems = (detail.items || []).map((item: any, index) => {
      const quantity = Number(item.quantity ?? item.qty ?? 0);
      const unitPrice = Number(item.original_price ?? item.unit_price ?? item.sprice ?? 0);
      const amount = Number(item.amount ?? item.total ?? quantity * unitPrice);
      const itemName = item.item_name || item.name || item.item_code || item.itemCode || `Item ${index + 1}`;

      return {
        code: item.item_code || item.itemCode || item.code || String(item.item_id ?? index + 1),
        name: quantity < 0 ? `${itemName} (RETURN)` : itemName,
        quantity: Math.abs(quantity),
        price: unitPrice,
        discount_percentage: Number(item.discount ?? item.linedisval ?? 0),
        discount_amount: Number(item.discount_amount ?? item.linedisamt ?? 0),
        total: Math.abs(amount),
      };
    });

    const paymentBreakdown = (detail.payments || []).map((payment) => ({
      label: payment.paytypedesc ? `${payment.paymode} - ${payment.paytypedesc}` : payment.paymode,
      amount: Number(payment.amount || 0),
    }));

    const totalQuantity = saleItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    return {
      store_name: systemSetup?.setup_comname || 'Rajida POS System',
      store_address: systemSetup?.setup_comaddress || 'Main Branch',
      store_tele: systemSetup?.setup_comtele || '-',
      store_logo: systemSetup?.setup_comlogo || undefined,
      store_inv_notes: (systemSetup as any)?.setup_invnotes || undefined,
      store_tin: systemSetup?.setup_tinno || undefined,
      store_reg_no: systemSetup?.setup_regno || undefined,
      tax_per: (detail as any).tax_per ?? null,
      customer_address: (detail as any).customer_address || undefined,
      customer_tin_no: (detail as any).customer_tin_no || undefined,
      customer_phone: (detail as any).customer_phone || undefined,
      invoice_number: detail.invoice_no,
      date_time: createdAt ? createdAt.toLocaleString() : detail.created_at || undefined,
      date: createdAt ? formatInvoiceDate(createdAt) : undefined,
      time: createdAt ? createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : undefined,
      cashier_id: detail.cashier_id || '-',
      cashier_name: billingPartyDetails.cashierName || user?.user_name || 'System',
      customer_code: detail.customer_code || '-',
      customer_name: detail.customer_name || 'Walk-in Customer',
      transaction_type: hasReturnItems ? 'Return' : 'Sales',
      payment_method: (detail.payments || []).map((payment) => payment.paytypedesc || payment.paymode).filter(Boolean).join(', ') || 'Cash',
      payment_breakdown: paymentBreakdown,
      pay_mode: (detail.payments || []).map((payment) => `${payment.paymode}${payment.paytypedesc ? ` ${payment.paytypedesc}` : ''}`).join(' | ') || 'Cash',
      subtotal: Number(detail.gross_amount ?? 0),
      discount_label: 'Bill Discount',
      discount_percentage: Number(detail.gross_amount ? ((detail.bill_discount_amount || 0) / detail.gross_amount) * 100 : 0),
      discount_amount: Number(detail.bill_discount_amount ?? 0),
      promotion_discount_amount: Number(detail.promotion_discount_amount ?? 0),
      total_amount: Number(detail.net_amount ?? 0),
      total_items: saleItems.length,
      total_quantity: totalQuantity,
      paid_amount: Number(detail.total_paid ?? 0),
      change_amount: Number(detail.change_amount ?? 0),
      promotions: (detail.promotions || []).map((promo) => ({
        promo_code: promo.promo_code || undefined,
        promo_name: promo.promo_name || undefined,
        promo_free_item: promo.promo_free_item || undefined,
        promo_free_voucher: promo.promo_free_voucher || undefined,
      })),
      items: saleItems,
    };
  };

  const handleReceiptPrintRequest = (receipt: RecentInvoiceResponse) => {
    // Tax invoices can only be reprinted in A4 - never let POS come in pre-selected.
    if (receipt.tax_per != null && printPaperSize !== 'A4') {
      setPrintPaperSize('A4');
    }
    setReceiptToPrint(receipt);
  };

  const handleConfirmReceiptPrint = async () => {
    if (!receiptToPrint) {
      return;
    }

    setIsReceiptPrintLoading(true);

    try {
      const invoiceDetail = await getInvoiceDetail(receiptToPrint.invoice_no);
      const isTaxInvoice = (invoiceDetail as any).tax_per != null;
      if (isTaxInvoice && printPaperSize !== 'A4') {
        toast.showError('Tax invoices can only be reprinted in A4.');
        setPrintPaperSize('A4');
        return;
      }
      const payload = {
        ...buildPosPrintPayloadFromInvoiceDetail(invoiceDetail),
        is_reprint: true,
      };
      printPosBill(payload, printConfig, { paperSize: printPaperSize, onAfterPrint: focusBarcodeInput });
      toast.showSuccess(`Invoice ${receiptToPrint.invoice_no} sent to print.`);
      setReceiptToPrint(null);
    } catch (error) {
      toast.showError('Failed to print the selected receipt.');
    } finally {
      setIsReceiptPrintLoading(false);
    }
  };

  const printSavedInvoice = async (invoiceNo: string) => {
    const invoiceDetail = await getInvoiceDetail(invoiceNo);
    const payload = buildPosPrintPayloadFromInvoiceDetail(invoiceDetail);

    printPosBill(payload, printConfig, { paperSize: printPaperSize, onAfterPrint: focusBarcodeInput });
  };

  // POS Print Configuration Hook
  const { config: printConfig } = usePosPrintConfig();

  /**
   * Build POS print payload from current billing data
   */
  const buildPosPrintPayload = (forcedInvoiceNo?: string): PosPrintPayload => {
    const invoiceNumber = forcedInvoiceNo || `INV-${Date.now().toString().slice(-7)}`;
    const now = new Date();
    const dateStr = formatInvoiceDate(now);
    const timeStr = now.toLocaleTimeString();

    // Identify return items (negative quantities)
    const returnItems = items.filter((item) => item.quantity < 0);
    const saleItems = items.filter((item) => item.quantity >= 0);
    const hasReturns = returnItems.length > 0;
    const hasSales = saleItems.length > 0;
    const isReturnOnly = hasReturns && !hasSales;

    return {
      store_name: systemSetup?.setup_comname || "Rajida POS System",
      store_address: systemSetup?.setup_comaddress || "Main Branch",
      store_tele: systemSetup?.setup_comtele || "-",
      store_logo: systemSetup?.setup_comlogo || undefined,
      store_inv_notes: (systemSetup as any)?.setup_invnotes || undefined,
      store_tin: systemSetup?.setup_tinno || undefined,
      store_reg_no: systemSetup?.setup_regno || undefined,
      tax_per: isVatInvoice ? Number(systemSetup?.setup_tax || 0) : null,
      customer_address: [selectedCustomer?.cus_add1, selectedCustomer?.cus_add2, selectedCustomer?.cus_add3, selectedCustomer?.cus_add4]
        .filter((part) => part && part.trim())
        .join(', ') || undefined,
      customer_tin_no: selectedCustomer?.cus_tinno || undefined,
      customer_phone: selectedCustomer?.cus_tep1 || selectedCustomer?.cus_tep2 || undefined,
      invoice_number: invoiceNumber,
      date: dateStr,
      time: timeStr,
      cashier_id: billingPartyDetails.cashierId || "-",
      cashier_name: billingPartyDetails.cashierName || "System",
      customer_code: billingPartyDetails.customerCode || "-",
      customer_name: billingPartyDetails.customerName || "Walk-in Customer",
      transaction_type: isReturnOnly ? "Return" : hasReturns ? "Mixed Sale/Return" : "Sales",
      payment_method: appliedPayments.map((paymentEntry) => paymentEntry.paymode).join(', ') || selectedPaymode || "Cash",
      payment_breakdown: appliedPayments.map((paymentEntry) => ({
        label: paymentEntry.details ? `${paymentEntry.label} (${paymentEntry.details})` : paymentEntry.label,
        amount: paymentEntry.amount,
      })),
      pay_mode: appliedPayments.map((paymentEntry) => {
        const baseLabel = `${paymentEntry.label} ${paymentEntry.amount.toFixed(2)}`;
        return paymentEntry.details ? `${baseLabel} ${paymentEntry.details}` : baseLabel;
      }).join(' | ') || paymentModes.find(m => m.paycode === selectedPaymode)?.paytypedesc || selectedPaymode || "Cash",
      subtotal: payment.subtotal,
      discount_label: "Bill Discount",
      discount_percentage: Number(billDiscount),
      discount_amount: payment.billDiscountAmount,
      promotion_discount_amount: payment.promotionDiscountAmount,
      total_amount: payment.totalDue,
      total_items: items.length,
      total_quantity: totalQuantity,
      paid_amount: payment.paymentReceived,
      change_amount: Number(payment.change) || 0,
      promotions: appliedPromotions.map((promo) => ({
        promo_code: promo.promo_code || undefined,
        promo_name: promo.promo_name || undefined,
        promo_free_item: promo.promo_free_item || undefined,
        promo_free_voucher: promo.promo_free_voucher || undefined,
      })),
      items: items.map((item) => ({
        code: item.itemCode || item.id,
        name: item.quantity < 0 ? `${item.name} (RETURN)` : item.name,
        quantity: Math.abs(item.quantity),
        price: item.basePrice,
        discount_percentage: item.discount,
        discount_amount: item.discountAmount ?? 0,
        total: Math.abs(item.total),
      })),
    };
  };

  /**
   * Build POS print payload for a saved order/quotation.
   * Mirrors buildPosPrintPayload but omits payment details, since an order
   * has no payment collected against it yet.
   */
  const buildQuotationPrintPayload = (orderNo: string): PosPrintPayload => {
    const now = new Date();
    const dateStr = formatInvoiceDate(now);
    const timeStr = now.toLocaleTimeString();

    return {
      store_name: systemSetup?.setup_comname || "Rajida POS System",
      store_address: systemSetup?.setup_comaddress || "Main Branch",
      store_tele: systemSetup?.setup_comtele || "-",
      store_logo: systemSetup?.setup_comlogo || undefined,
      store_inv_notes: (systemSetup as any)?.setup_invnotes || undefined,
      store_tin: systemSetup?.setup_tinno || undefined,
      store_reg_no: systemSetup?.setup_regno || undefined,
      tax_per: null,
      document_label: 'QUOTATION',
      invoice_number_label: 'Quotation No :',
      hide_payment_section: true,
      total_label: 'Total',
      footer_notes: (systemSetup as any)?.setup_quotenotes || undefined,
      customer_address: [selectedCustomer?.cus_add1, selectedCustomer?.cus_add2, selectedCustomer?.cus_add3, selectedCustomer?.cus_add4]
        .filter((part) => part && part.trim())
        .join(', ') || undefined,
      customer_tin_no: selectedCustomer?.cus_tinno || undefined,
      customer_phone: selectedCustomer?.cus_tep1 || selectedCustomer?.cus_tep2 || undefined,
      invoice_number: orderNo,
      date: dateStr,
      time: timeStr,
      cashier_id: billingPartyDetails.cashierId || "-",
      cashier_name: billingPartyDetails.cashierName || "System",
      customer_code: billingPartyDetails.customerCode || "-",
      customer_name: billingPartyDetails.customerName || "Walk-in Customer",
      transaction_type: "Quotation",
      subtotal: payment.subtotal,
      discount_label: "Bill Discount",
      discount_percentage: Number(billDiscount),
      discount_amount: payment.billDiscountAmount,
      promotion_discount_amount: payment.promotionDiscountAmount,
      total_amount: payment.totalDue,
      total_items: items.length,
      total_quantity: totalQuantity,
      promotions: appliedPromotions.map((promo) => ({
        promo_code: promo.promo_code || undefined,
        promo_name: promo.promo_name || undefined,
        promo_free_item: promo.promo_free_item || undefined,
        promo_free_voucher: promo.promo_free_voucher || undefined,
      })),
      items: items.map((item) => ({
        code: item.itemCode || item.id,
        name: item.name,
        quantity: Math.abs(item.quantity),
        price: item.basePrice,
        discount_percentage: item.discount,
        discount_amount: item.discountAmount ?? 0,
        total: Math.abs(item.total),
      })),
    };
  };

  const handleConfirmQuotationPrint = () => {
    if (!quotationToPrint) {
      return;
    }

    setIsQuotationPrintLoading(true);

    try {
      printPosBill(quotationToPrint.payload, printConfig, { paperSize: 'A4', onAfterPrint: focusBarcodeInput });
      toast.showSuccess(`Quotation ${quotationToPrint.orderNo} sent to print.`);
      setQuotationToPrint(null);
    } catch (error) {
      toast.showError('Failed to print the quotation.');
    } finally {
      setIsQuotationPrintLoading(false);
    }
  };

  /**
   * Enhanced print handler with dynamic POS layout
   */
  const handlePrint = (forcedInvoiceNo?: string) => {
    if (items.length === 0) {
      toast.showError("No items to print. Please add items to the bill.");
      return;
    }

    try {
      const payload = buildPosPrintPayload(forcedInvoiceNo);
      printPosBill(payload, printConfig, { paperSize: printPaperSize, onAfterPrint: focusBarcodeInput });
    } catch (error) {
      // eslint-disable-next-line no-console
      toast.showError("Failed to generate print. Please try again.");
      // Fallback to default print if custom print fails
      window.print();
      /* Optionally, send error to error tracking service */
    }
  };

  const numpadButtons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['0', '00', '.'],
  ];

  const paymentMethodButtonsFromApi: PaymentMethodButton[] = paymentModes.map((mode) => {
    const description = (mode.paytypedesc || mode.paycode).trim();
    return {
      id: mode.paycode,
      label: description,
      icon: getPaymentMethodIcon(mode.paycode, description),
    };
  });

  const fallbackPaymentMethodButtons: PaymentMethodButton[] = [
    { id: 'debit', label: 'Debit Card', icon: 'card' },
    { id: 'cash', label: 'Cash', icon: 'cash' },
    { id: 'other', label: 'Other', icon: 'other' },
  ];

  const paymentMethodButtons = [
    { id: 'debit', label: 'Debit Card', icon: '💳' },
    { id: 'cash', label: 'Cash', icon: '💵' },
    { id: 'other', label: 'Other', icon: '⊙' },
  ];

  void paymentMethodButtons;

  const billingLeftNavActions = [
    {
      id: 'search-item',
      label: 'Search item',
      title: 'Search item',
      icon: 'search' as const,
      active: searchModalOpen,
      onClick: () => setSearchModalOpen(true),
    },
    {
      id: 'scan-barcode',
      label: 'Scan barcode',
      title: 'Scan barcode',
      icon: 'barcode' as const,
      onClick: handleScanBarcode,
    },
    {
      id: 'price-history',
      label: 'Price history',
      title: 'Price history',
      icon: 'history' as const,
      onClick: () => undefined,
    },
    {
      id: 'promotions',
      label: 'Promotions',
      title: 'Promotions',
      icon: 'promotions' as const,
      onClick: () => setShowPromotionsModal(true),
    },
  ];

  return (
    <Box ref={billingContainerRef} className="billing-screen-container">
      {/* Loading and Error States */}
      {loading && (
        <Box className="billing-loading-overlay">
          <Stack space="medium">
            <Text weight="strong" size="large">
              Loading billing data...
            </Text>
          </Stack>
        </Box>
      )}

      {error && (
        <Box className="billing-error-overlay">
          <Stack space="medium">
            <Text weight="strong" size="large" tone="critical">
              Error Loading Data
            </Text>
            <Text tone="secondary">{error}</Text>
          </Stack>
        </Box>
      )}

      {!loading && !error && (
        <>
          <Box className="billing-main-section">
            <Box className="billing-workspace">
              <div className="billing-left-nav" aria-label="Billing quick actions">
                {billingLeftNavActions.map((action) => {
                  const hasActivePromotions = action.id === 'promotions' && hasCurrentlyValidPromotions;
                  const hasInactivePromotions = action.id === 'promotions' && !hasActivePromotions && promotions.length > 0;
                  return (
                    <button
                      key={action.id}
                      type="button"
                      className={`billing-left-nav-btn ${action.active ? 'billing-left-nav-btn-active' : ''} ${hasActivePromotions ? 'billing-promotions-btn-active' : ''} ${hasInactivePromotions ? 'billing-promotions-btn-inactive' : ''}`}
                      title={action.title}
                      onClick={() => {
                        // For Promotions button: only show popup when there are valid promotions (green state)
                        if (action.id === 'promotions' && !hasCurrentlyValidPromotions) {
                          return;
                        }
                        action.onClick();
                      }}
                    >
                      <span className="billing-left-nav-icon">
                        {renderBillingNavIcon(action.icon)}
                      </span>
                      <span className="billing-left-nav-label">{action.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Main Content */}
              <Box className="billing-content">
                <Box className="billing-left-column">
                  {/* Left Panel - Items Section */}
                  <Box className="billing-items-panel">
                    <Box padding="small" background="surface" className="billing-items-panel-body">
                      <div className="billing-items-panel-stack">
                        <div className="billing-items-layout">
                          <Box className="billing-party-details">
                            <div className="billing-party-detail-card">
                              <span className="billing-party-detail-label">Customer</span>
                              <div className="billing-party-detail-pair">
                                <span className="billing-party-detail-meta">Code</span>
                                <span className="billing-party-detail-value">{billingPartyDetails.customerCode}</span>
                              </div>
                              <div className="billing-party-detail-pair">
                                <span className="billing-party-detail-meta">Name</span>
                                <span className="billing-party-detail-value">{billingPartyDetails.customerName}</span>
                              </div>
                            </div>
                            <div className="billing-party-detail-card">
                              <span className="billing-party-detail-label">Sales Rep</span>
                              <div className="billing-party-detail-pair">
                                <span className="billing-party-detail-meta">ID</span>
                                <span className="billing-party-detail-value">{billingPartyDetails.salesRepId}</span>
                              </div>
                              <div className="billing-party-detail-pair">
                                <span className="billing-party-detail-meta">Name</span>
                                <span className="billing-party-detail-value">{billingPartyDetails.salesRepName}</span>
                              </div>
                            </div>
                            <div className="billing-party-detail-card">
                              <span className="billing-party-detail-label">Cashier</span>
                              <div className="billing-party-detail-pair">
                                <span className="billing-party-detail-meta">ID</span>
                                <span className="billing-party-detail-value">{billingPartyDetails.cashierId}</span>
                              </div>
                              <div className="billing-party-detail-pair">
                                <span className="billing-party-detail-meta">Name</span>
                                <span className="billing-party-detail-value">{billingPartyDetails.cashierName}</span>
                              </div>
                              <div className="billing-party-detail-pair">
                                <span className="billing-party-detail-meta">Counter</span>
                                <span className="billing-party-detail-value">{getStationId() || '-'}</span>
                              </div>
                            </div>
                          </Box>

                          <Box className="billing-items-header">
                            {loadedReturnInvoice && (
                              <Box className="return-mode-banner">
                                <Text size="small" tone="secondary">
                                  Original invoice: {loadedReturnInvoice.invoice_no}
                                </Text>
                              </Box>
                            )}
                            <Box className="billing-items-header-controls">
                              <Box className="billing-barcode-field">
                                <input
                                  ref={barcodeInputRef}
                                  id="billing-barcode-input"
                                  type="text"
                                  aria-label="Enter item barcode"
                                  placeholder="Enter barcode"
                                  value={barcodeInput}
                                  onChange={(event) => setBarcodeInput(event.target.value)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      if (!barcodeInput.trim()) {
                                        // Nothing to scan: move on to payment (bills with nothing to collect go straight to Invoice)
                                        if (items.length > 0) {
                                          if (payment.totalDue > 0) {
                                            focusPaymentAmount();
                                          } else {
                                            focusInvoiceButton();
                                          }
                                        }
                                        return;
                                      }
                                      handleBarcodeSubmit();
                                    }
                                  }}
                                  className="billing-barcode-input"
                                />
                              </Box>

                              <div className="billing-add-item-btn">
                                <Button
                                  onClick={() => setSearchModalOpen(true)}
                                  variant="solid"
                                  tone="brandAccent"
                                >
                                  + Add Item
                                </Button>
                              </div>
                            </Box>
                          </Box>

                          <div className="billing-items-scroll-area">
                            {items.length > 0 ? (
                              <Box className="billing-items-table">
                                <table>
                                  <thead>
                                    <tr>
                                      <th className="billing-line-no">#</th>
                                      <th>Name</th>
                                      <th>Price</th>
                                      <th>Disc %</th>
                                      <th>Disc Amt</th>
                                      <th>Qty</th>
                                      <th>Total</th>
                                      <th>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((item, index) => (
                                      <tr key={item.id} className={item.lineType === 'RETURN' || item.quantity < 0 ? 'billing-return-line-row' : ''}>
                                        <td className="billing-line-no">{index + 1}</td>
                                        <td>
                                          {(item.lineType === 'RETURN' || item.quantity < 0) && (
                                            <span className="return-badge">RETURN</span>
                                          )}
                                          <span>{item.name}</span>
                                        </td>
                                        <td>{item.price.toFixed(2)}</td>
                                        <td>
                                          <input
                                            type="number"
                                            value={item.discount}
                                            aria-label={`Discount percentage for ${item.name}`}
                                            onChange={(e) => handleLineDiscountChange(item.id, e.target.value)}
                                            onKeyDown={handleDiscountKeyDown}
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            disabled={(item.discountAmount || 0) > 0}
                                            title={(item.discountAmount || 0) > 0 ? 'Clear the discount amount to use a percentage' : undefined}
                                            className="discount-input"
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="number"
                                            value={item.discountAmount || 0}
                                            aria-label={`Discount amount per unit for ${item.name}`}
                                            onChange={(e) => handleLineDiscountAmountChange(item.id, e.target.value)}
                                            onKeyDown={handleDiscountKeyDown}
                                            min="0"
                                            max={item.basePrice}
                                            step="0.01"
                                            disabled={item.discount > 0 || item.lineType === 'RETURN' || item.quantity < 0}
                                            title={
                                              item.lineType === 'RETURN' || item.quantity < 0
                                                ? 'Discount amount is not allowed on return lines'
                                                : item.discount > 0
                                                  ? 'Clear the discount % to use an amount'
                                                  : undefined
                                            }
                                            className="discount-input"
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="number"
                                            value={item.quantity}
                                            aria-label={`Quantity for ${item.name}`}
                                            step={isWeightedItem(item) ? '0.001' : '1'}
                                            inputMode={isWeightedItem(item) ? 'decimal' : 'numeric'}
                                            onChange={(e) => {
                                              const parsed = Number(e.target.value);
                                              handleQuantityChange(item.id, parsed);
                                            }}
                                            className={`qty-input ${item.lineType === 'RETURN' || item.quantity < 0 ? 'qty-input-return' : ''}`}
                                          />
                                        </td>
                                        <td className={item.lineType === 'RETURN' || item.quantity < 0 ? 'billing-return-line-total' : ''}>
                                          {(item.lineType === 'RETURN' || item.quantity < 0 ? -Math.abs(item.total) : item.total).toFixed(2)}
                                        </td>
                                        <td>
                                          <button
                                            onClick={() => handleRemoveItem(item.id)}
                                            className="remove-btn"
                                            title="Remove item"
                                          >
                                            {renderTrashIcon()}
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </Box>
                            ) : (
                              <div className="billing-items-empty-state">
                                <Text tone="secondary">No items in cart</Text>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Box>
                  </Box>

                  <Box className="billing-bottom-bar">
                    <div className="billing-bottom-summary">
                      <div className="billing-bottom-label">Quantity : {totalQuantity}</div>
                      <div className="billing-bottom-total">Total : {payment.totalDue.toFixed(2)}</div>
                    </div>
                    <div className="billing-bottom-actions">
                      <button
                        className="billing-bottom-btn charge"
                        onClick={handlePlaceOrder}
                        disabled={items.length === 0 || isOrderSubmitting || systemSetup?.setup_orders === false}
                      >
                        {isOrderSubmitting ? 'Saving...' : editingOrderNo ? 'Update Order' : 'Place Order'}
                      </button>
                      <button
                        className="billing-bottom-btn clear"
                        onClick={handleClearCart}
                        disabled={items.length === 0}
                      >
                        <span className="billing-bottom-btn-icon">{renderTrashIcon()}</span>
                        <span>Clear</span>
                      </button>
                    </div>
                  </Box>
                </Box>

                {/* Right Panel - Payment Section */}
                <Box className="billing-payment-panel">
                  <div className="billing-payment-stack">
                    {/* Payment Summary */}
                    <Box padding="small" background="surface" className="payment-summary">
                      <Box className="payment-info-card">
                        {isCreditPaymode && isWalkInCustomer(selectedCustomer) && (
                          <div className="payment-summary-alert">
                            <Text tone="critical" size="small">
                              Credit payment needs a customer selection. Walk in customer is not allowed.
                            </Text>
                          </div>
                        )}
                        {loadedReturnInvoice && (
                          <div className="payment-summary-return-meta">
                            {loadedReturnInvoice.payments.length > 0 && (
                              <div className="payment-summary-return-payments">
                                <Text size="small" tone="secondary">Original payment methods</Text>
                                {loadedReturnInvoice.payments.map((paymentRow, index) => (
                                  <div key={`${paymentRow.paymode}-${index}`} className="payment-summary-return-payment-row">
                                    <span>{paymentRow.paytypedesc || paymentRow.paymode}</span>
                                    <span>{Math.abs(paymentRow.amount).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="payment-info-row">
                          <Text tone="secondary">Gross amount</Text>
                          <Box className="amount-primary">
                            <Text weight="strong" size="large">{payment.subtotal.toFixed(2)}</Text>
                          </Box>
                        </div>

                        <div className="payment-info-row">
                          <Text tone="secondary">Due amount</Text>
                          <Box className="amount-primary">
                            <Text weight="strong" size="large">{payment.outstandingAmount.toFixed(2)}</Text>
                          </Box>
                        </div>

                        <div className="payment-info-row payment-info-row-discount">
                          <Text tone="secondary">Bill discount (%)</Text>
                          <div className="payment-discount-input-wrap">
                            <input
                              type="number"
                              value={billDiscount}
                              aria-label="Bill discount percentage"
                              onChange={(event) => handleBillDiscountChange(event.target.value)}
                              onKeyDown={handleDiscountKeyDown}
                              min="0"
                              max="100"
                              step="0.01"
                              className="payment-discount-input"
                            />
                            <span className="payment-discount-amount">
                              -{payment.billDiscountAmount.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {hasCurrentlyValidPromotions && (
                          <div className="payment-info-row payment-info-row-discount">
                            <Text tone="secondary">
                              Promotion {appliedPromotions.length > 0 ? `(${appliedPromotions.length})` : '(0)'}
                            </Text>
                            <Box className="amount-primary">
                              <Text weight="strong" size="large" tone="positive">
                                -{payment.promotionDiscountAmount.toFixed(2)}
                              </Text>
                            </Box>
                          </div>
                        )}

                        <div className="payment-info-row payment-info-row-discount">
                          <Text tone="secondary">Net amount</Text>
                          <Box className="amount-primary">
                            <Text weight="strong" size="large">{payment.totalDue.toFixed(2)}</Text>
                          </Box>
                        </div>

                        {loyaltyPoints > 0 && (
                          <div className="payment-info-row payment-info-row-discount">
                            <Text tone="secondary">Loyalty Points</Text>
                            <Box className="amount-primary">
                              <Text weight="strong" size="large">{loyaltyPoints.toFixed(2)}</Text>
                            </Box>
                          </div>
                        )}
                      </Box>
                    </Box>

                    {/* Numeric Keypad */}
                    <Box padding="small" background="surface" className="numpad-section">
                      <div className="numpad-layout">
                        <div className="numpad-main">
                          <div className="numpad-display" ref={amountDisplayRef} tabIndex={-1}>
                            <Text weight="strong" size="standard" align="center">
                              {selectedPaymodeAmount || '0'}
                            </Text>
                          </div>

                          <div className="numpad-grid">
                            {numpadButtons.flat().map((btn) => (
                              <button
                                key={btn}
                                onClick={() => handleNumpadClick(btn)}
                                className="numpad-btn"
                                disabled={!selectedPaymode}
                              >
                                {btn}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="numpad-actions-column">
                          <button
                            onClick={() => handleNumpadClick('Cancel')}
                            className="numpad-btn action-btn cancel-btn"
                            disabled={!selectedPaymode}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleNumpadClick('Delete')}
                            className="numpad-btn action-btn delete-btn"
                            disabled={!selectedPaymode}
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => handleNumpadClick('Enter')}
                            className="numpad-btn action-btn enter-btn"
                            disabled={!selectedPaymode}
                          >
                            Enter
                          </button>
                        </div>
                      </div>

                      <div className="numpad-payment-totals">
                        <div className="numpad-payment-total">
                          <span className="numpad-payment-total-label">Paid Amount</span>
                          <span className="numpad-payment-total-value">{payment.allocatedAmount.toFixed(2)}</span>
                        </div>
                        <div className="numpad-payment-total numpad-payment-total-change">
                          <span className="numpad-payment-total-label">Change</span>
                          <span className="numpad-payment-total-value">{payment.change.toFixed(2)}</span>
                        </div>
                      </div>
                    </Box>

                    {/* Payment Method Buttons */}
                    <Box className="payment-method-buttons-section">
                      <div className="payment-mode-editor-header">
                        <div className="payment-mode-editor-label">
                          Editing: {selectedPaymentMode?.paytypedesc || selectedPaymode}
                        </div>
                        {selectedPaymentMode && isCardPaymentMode(selectedPaymentMode.paycode, selectedPaymentMode.paytypedesc || selectedPaymentMode.paycode) ? (
                          <button
                            type="button"
                            className="payment-card-launch-btn"
                            onClick={() => openCardPaymentModal(selectedPaymentMode.paycode)}
                          >
                            Edit card details
                          </button>
                        ) : null}
                      </div>
                      <div className="payment-method-buttons-grid">
                        {(paymentMethodButtonsFromApi.length > 0 ? paymentMethodButtonsFromApi : fallbackPaymentMethodButtons).map((btn) => (
                          <button
                            key={btn.id}
                            onClick={() => handleSelectPaymode(btn.id)}
                            className={`payment-method-btn ${selectedPaymode === btn.id ? 'payment-method-btn-active' : ''}`}
                            title={btn.label}
                          >
                            <div className="payment-method-icon">{renderPaymentMethodIcon(btn.icon)}</div>
                            <div className="payment-method-label">{btn.label}</div>
                          </button>
                        ))}
                      </div>
                      <div className="payment-allocation-list">
                        {appliedPayments.length > 0 ? appliedPayments.map((paymentEntry) => (
                          <div key={paymentEntry.paymode} className="payment-allocation-row">
                            <div className="payment-allocation-copy">
                              <span>{paymentEntry.label}</span>
                              {paymentEntry.details ? (
                                <span className="payment-allocation-meta">{paymentEntry.details}</span>
                              ) : null}
                            </div>
                            <div className="payment-allocation-actions">
                              <span>{paymentEntry.amount.toFixed(2)}</span>
                              <button
                                type="button"
                                className="payment-allocation-remove-btn"
                                onClick={() => {
                                  if (paymentEntry.paymode === 'CSH') {
                                    setIsCashAutoFilled(false);
                                  }
                                  setPaymentAllocations((prev) => {
                                    const nextAllocations = { ...prev };
                                    delete nextAllocations[paymentEntry.paymode];
                                    return Object.keys(nextAllocations).length > 0 ? nextAllocations : { CSH: '' };
                                  });

                                  setCardPaymentDetails((prev) => {
                                    const nextDetails = { ...prev };
                                    delete nextDetails[paymentEntry.paymode];
                                    return nextDetails;
                                  });

                                  if (selectedPaymode === paymentEntry.paymode) {
                                    const remainingPaymode = appliedPayments.find(
                                      (paymentRow) => paymentRow.paymode !== paymentEntry.paymode,
                                    )?.paymode || 'CSH';
                                    setSelectedPaymode(remainingPaymode);
                                  }
                                }}
                                aria-label={`Remove ${paymentEntry.label}`}
                                title={`Remove ${paymentEntry.label}`}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )) : (
                          <div className="payment-allocation-empty">No payment amount entered yet.</div>
                        )}
                      </div>
                    </Box>

                    <Box className="billing-payment-footer">
                      <div className="billing-print-size-switch" aria-label="Invoice print size selector">
                        <label className="billing-vat-checkbox">
                          <input
                            type="checkbox"
                            checked={isVatInvoice}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setIsVatInvoice(checked);
                              if (checked) {
                                setPrintPaperSize('A4');
                              }
                            }}
                          />
                          <span>VAT</span>
                        </label>
                        <div className="billing-print-size-options" role="group" aria-label="Select invoice print size">
                          <button
                            type="button"
                            className={`billing-print-size-btn ${printPaperSize === 'POS' ? 'billing-print-size-btn-active' : ''}`}
                            onClick={() => setPrintPaperSize('POS')}
                            disabled={isVatInvoice}
                            title={isVatInvoice ? 'Tax invoices must be printed in A4' : undefined}
                          >
                            POS
                          </button>
                          <button
                            type="button"
                            className={`billing-print-size-btn ${printPaperSize === 'A4' ? 'billing-print-size-btn-active' : ''}`}
                            onClick={() => setPrintPaperSize('A4')}
                          >
                            A4
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        ref={invoiceButtonRef}
                        className="billing-payment-action-btn"
                        onClick={handleInvoice}
                        disabled={items.length === 0 || isInvoiceSubmitting}
                      >
                        {isInvoiceSubmitting ? 'Submitting...' : `Invoice (${printPaperSize})`}
                      </button>
                    </Box>
                  </div>
                </Box>
              </Box>
            </Box>

          </Box>

          {/* Right Vertical Navigation Bar */}
          <div className="billing-vertical-nav" ref={billingVerticalNavRef}>
            {systemSetup?.setup_orders !== false && (
              <button
                className={`billing-vertical-nav-btn ${activeSidebarPanel === 'orders' ? 'billing-vertical-nav-btn-active' : ''}`}
                title={activeSidebarPanel === 'orders' ? 'Hide Orders' : 'Show Orders'}
                onClick={() => toggleSidebarPanel('orders')}
              >
                <div className="nav-btn-icon">O</div>
                <div className="nav-btn-label">Orders</div>
              </button>
            )}
            <button
              className={`billing-vertical-nav-btn ${activeSidebarPanel === 'member' ? 'billing-vertical-nav-btn-active' : ''}`}
              title={activeSidebarPanel === 'member' ? 'Hide Customer Selector' : 'Show Customer Selector'}
              onClick={() => toggleSidebarPanel('member')}
            >
              <div className="nav-btn-icon">👤</div>
              <div className="nav-btn-label">Customer</div>
            </button>
            <button
              className={`billing-vertical-nav-btn ${activeSidebarPanel === 'staff' ? 'billing-vertical-nav-btn-active' : ''}`}
              title={activeSidebarPanel === 'staff' ? 'Hide Staff Selector' : 'Show Staff Selector'}
              onClick={() => toggleSidebarPanel('staff')}
            >
              <div className="nav-btn-icon">👨‍💼</div>
              <div className="nav-btn-label">Staff</div>
            </button>
            <button
              className={`billing-vertical-nav-btn ${activeSidebarPanel === 'receipts' ? 'billing-vertical-nav-btn-active' : ''}`}
              title={activeSidebarPanel === 'receipts' ? 'Hide Receipts' : 'Show Receipts'}
              onClick={() => toggleSidebarPanel('receipts')}
            >
              <div className="nav-btn-icon">🧾</div>
              <div className="nav-btn-label">Receipts</div>
            </button>
            <button
              className={`billing-vertical-nav-btn ${activeSidebarPanel === 'returns' ? 'billing-vertical-nav-btn-active' : ''}`}
              title={activeSidebarPanel === 'returns' ? 'Hide Returns' : 'Show Returns'}
              onClick={() => toggleSidebarPanel('returns')}
            >
              <div className="nav-btn-icon">↩️</div>
              <div className="nav-btn-label">Returns</div>
            </button>
          </div>

          {activeSidebarPanel === 'orders' && (
            <aside className="billing-orders-sidebar" ref={billingSidebarRef}>
              <Box className="billing-orders-panel">
                <Box className="billing-orders-header">
                  <div className="billing-selector-heading">
                    <Text weight="strong" size="large">
                      Orders
                    </Text>
                  </div>
                </Box>

                <Box className="billing-orders-table-wrap">
                  {orders.length > 0 ? (
                    <table className="billing-orders-table">
                      <thead>
                        <tr>
                          <th>Number</th>
                          <th>Amount</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.id}>
                            <td>
                              <div className="billing-order-number">
                                <span className="billing-order-number-main">{order.number}</span>
                                <span className="billing-order-number-meta">
                                  {order.itemCount} items
                                </span>
                              </div>
                            </td>
                            <td>{order.amount.toFixed(2)}</td>
                            <td>
                              <div className="billing-order-action-group">
                                <button
                                  type="button"
                                  className="billing-order-action-btn billing-order-action-btn-edit"
                                  onClick={() => handleEditOrder(order)}
                                  aria-label={`Edit order ${order.number}`}
                                  title={`Edit order ${order.number}`}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                  >
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="billing-order-action-btn billing-order-action-btn-delete"
                                  onClick={() => handleDeleteOrder(order)}
                                  aria-label={`Delete order ${order.number}`}
                                  title={`Delete order ${order.number}`}
                                >
                                  {renderTrashIcon()}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="billing-orders-empty">
                      <Text tone="secondary">No orders yet</Text>
                    </div>
                  )}
                </Box>
              </Box>
            </aside>
          )}

          {activeSidebarPanel === 'member' && (
            <aside className="billing-orders-sidebar" ref={billingSidebarRef}>
              <Box className="billing-orders-panel">
                <Box className="billing-orders-header">
                  <div className="billing-selector-heading">
                    <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                      <Stack space="xsmall">
                        <Text weight="strong" size="large">
                          Customers
                        </Text>
                        <Text tone="secondary" size="small">
                          Search and select a customer by code or name
                        </Text>
                      </Stack>
                      <Button
                        size="small"
                        variant="solid"
                        tone="brandAccent"
                        onClick={() => setIsCustomerDrawerOpen(true)}
                      >
                        + New
                      </Button>
                    </Box>
                  </div>
                </Box>

                <div className="billing-selector-search-wrap">
                  <input
                    type="text"
                    value={memberSearchCode}
                    onChange={(event) => setMemberSearchCode(event.target.value)}
                    placeholder="Enter customer code or name"
                    aria-label="Search customer by code or name"
                    className="billing-selector-search-input"
                  />
                </div>

                <div className="billing-selector-list">
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.map((customer) => (
                      <button
                        key={String(customer.cus_id ?? customer.cus_code ?? 'customer')}
                        type="button"
                        className="billing-selector-item"
                        onClick={() => handleSelectCustomer(customer)}
                      >
                        <span className="billing-selector-item-code">{customer.cus_code || '-'}</span>
                        <span className="billing-selector-item-name">{customer.cus_name || '-'}</span>
                      </button>
                    ))
                  ) : (
                    <div className="billing-selector-empty">
                      <Text tone="secondary">No customers found for that code.</Text>
                    </div>
                  )}
                </div>
              </Box>
            </aside>
          )}

          {activeSidebarPanel === 'staff' && (
            <aside className="billing-orders-sidebar" ref={billingSidebarRef}>
              <Box className="billing-orders-panel">
                <Box className="billing-orders-header">
                  <div className="billing-selector-heading">
                    <Text weight="strong" size="large">
                      Staff
                    </Text>
                    <Text tone="secondary" size="small">
                      Search and select a staff member by ID or name
                    </Text>
                  </div>
                </Box>

                <div className="billing-selector-search-wrap">
                  <input
                    type="text"
                    value={staffSearchCode}
                    onChange={(event) => setStaffSearchCode(event.target.value)}
                    placeholder="Enter staff ID or name"
                    aria-label="Search staff by ID or name"
                    className="billing-selector-search-input"
                  />
                </div>

                <div className="billing-selector-list">
                  {filteredStaffUsers.length > 0 ? (
                    filteredStaffUsers.map((staffUser) => (
                      <button
                        key={String(staffUser.user_id ?? staffUser.user_name ?? 'staff')}
                        type="button"
                        className="billing-selector-item"
                        onClick={() => handleSelectStaff(staffUser)}
                      >
                        <span className="billing-selector-item-code">{String(staffUser.user_id ?? '-')}</span>
                        <span className="billing-selector-item-name">{staffUser.user_name || '-'}</span>
                      </button>
                    ))
                  ) : (
                    <div className="billing-selector-empty">
                      <Text tone="secondary">No staff found for that ID.</Text>
                    </div>
                  )}
                </div>
              </Box>
            </aside>
          )}

          {activeSidebarPanel === 'receipts' && (
            <aside className="billing-orders-sidebar" ref={billingSidebarRef}>
              <Box className="billing-orders-panel">
                <Box className="billing-orders-header">
                  <div className="billing-selector-heading">
                    <Stack space="xsmall">
                      <Text weight="strong" size="large">
                        Recent Receipts
                      </Text>
                      <Box display="flex" alignItems="center" justifyContent="spaceBetween" gap="xsmall">
                        <Text tone="secondary" size="small">
                          {receiptDateResults !== null
                            ? `${receiptDateTotal} receipt${receiptDateTotal === 1 ? '' : 's'} found for the selected date range`
                            : 'View last 10 invoices, or search by date range'}
                        </Text>
                        {receiptDateResults !== null && (
                          <button
                            type="button"
                            className="billing-icon-btn billing-icon-btn-ghost billing-icon-btn-small"
                            onClick={handleClearReceiptDateSearch}
                            disabled={isReceiptDateSearching}
                            title="Clear filter"
                            aria-label="Clear date filter"
                          >
                            {renderCloseGlyphIcon(12)}
                          </button>
                        )}
                      </Box>
                    </Stack>
                  </div>
                </Box>

                <div className="billing-receipt-date-filter">
                  <div className="billing-receipt-date-field">
                    <label className="billing-receipt-date-label" htmlFor="billing-receipt-from-date">From</label>
                    <input
                      id="billing-receipt-from-date"
                      type="date"
                      value={receiptFromDate}
                      max={receiptToDate || undefined}
                      onChange={(event) => setReceiptFromDate(event.target.value)}
                      className="billing-selector-search-input billing-receipt-date-input"
                    />
                  </div>
                  <div className="billing-receipt-date-field">
                    <label className="billing-receipt-date-label" htmlFor="billing-receipt-to-date">To</label>
                    <input
                      id="billing-receipt-to-date"
                      type="date"
                      value={receiptToDate}
                      min={receiptFromDate || undefined}
                      onChange={(event) => setReceiptToDate(event.target.value)}
                      className="billing-selector-search-input billing-receipt-date-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="billing-icon-btn"
                    onClick={handleReceiptDateSearch}
                    disabled={isReceiptDateSearching}
                    title="Search"
                    aria-label="Search receipts by date range"
                  >
                    {renderSearchGlyphIcon(16)}
                  </button>
                </div>
                {receiptDateSearchError ? (
                  <Text tone="critical" size="small">{receiptDateSearchError}</Text>
                ) : null}

                <div className="billing-selector-list">
                  {(receiptDateResults !== null ? receiptDateResults : recentInvoices).length > 0 ? (
                    (receiptDateResults !== null ? receiptDateResults : recentInvoices).map((bill) => (
                      <button
                        key={bill.invoice_no}
                        type="button"
                        onClick={() => handleReceiptPrintRequest(bill)}
                        className="billing-selector-item billing-selector-item-receipt"
                      >
                        <Box display="flex" justifyContent="spaceBetween" width="full">
                          <Text weight="strong">{bill.invoice_no}</Text>
                          <Text weight="strong">{bill.amount.toFixed(2)}</Text>
                        </Box>
                        <Box display="flex" justifyContent="spaceBetween" width="full" marginTop="xsmall">
                          <Text size="small" tone="secondary">{bill.customer_name || 'Walk-in'}</Text>
                          <Text size="small" tone="secondary">
                            {bill.created_at
                              ? (receiptDateResults !== null
                                ? new Date(bill.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : new Date(bill.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
                              : '-'}
                          </Text>
                        </Box>
                      </button>
                    ))
                  ) : (
                    <div className="billing-selector-empty">
                      <Text tone="secondary">No receipts found.</Text>
                    </div>
                  )}
                </div>

                {receiptDateResults !== null && receiptDateTotal > RECEIPT_DATE_PAGE_SIZE && (
                  <div className="billing-receipt-pagination">
                    <button
                      type="button"
                      className="billing-icon-btn billing-icon-btn-ghost"
                      onClick={() => handleReceiptDatePageChange(receiptDatePage - 1)}
                      disabled={receiptDatePage <= 1 || isReceiptDateSearching}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>
                    <Text size="small" tone="secondary">
                      Page {receiptDatePage} of {Math.max(1, Math.ceil(receiptDateTotal / RECEIPT_DATE_PAGE_SIZE))}
                    </Text>
                    <button
                      type="button"
                      className="billing-icon-btn billing-icon-btn-ghost"
                      onClick={() => handleReceiptDatePageChange(receiptDatePage + 1)}
                      disabled={receiptDatePage >= Math.ceil(receiptDateTotal / RECEIPT_DATE_PAGE_SIZE) || isReceiptDateSearching}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                )}
              </Box>
            </aside>
          )}

          {activeSidebarPanel === 'returns' && (
            <aside className="billing-orders-sidebar" ref={billingSidebarRef}>
              <Box className="billing-orders-panel">
                <Box className="billing-orders-header">
                  <div className="billing-selector-heading">
                    <Stack space="xsmall">
                      <Text weight="strong" size="large">
                        Return Lookup
                      </Text>
                      <Text tone="secondary" size="small">
                        Optional: select a previous invoice to prefill negative return lines
                      </Text>
                    </Stack>
                  </div>
                </Box>

                {/* Invoice Search Bar */}
                <div className="billing-selector-search-wrap">
                  <input
                    type="text"
                    placeholder="Search by invoice # or customer..."
                    value={invoiceSearchTerm}
                    onChange={(e) => handleSearchInvoices(e.target.value)}
                    className="billing-selector-search-input"
                  />
                </div>

                <div className="billing-selector-list">
                  {(invoiceSearchTerm ? invoiceSearchResults : recentInvoices).length > 0 ? (
                    (invoiceSearchTerm ? invoiceSearchResults : recentInvoices).map((invoice) => (
                      <button
                        key={invoice.invoice_no}
                        type="button"
                        onClick={() => {
                          handleSelectInvoiceForReturn(invoice);
                          setInvoiceSearchTerm('');
                          setInvoiceSearchResults([]);
                        }}
                        className="billing-selector-item billing-selector-item-receipt"
                      >
                        <Box display="flex" justifyContent="spaceBetween" width="full">
                          <Text weight="strong">{invoice.invoice_no}</Text>
                          <Text weight="strong">{invoice.amount.toFixed(2)}</Text>
                        </Box>
                        <Box display="flex" justifyContent="spaceBetween" width="full" marginTop="xsmall">
                          <Text size="small" tone="secondary">{invoice.customer_name || 'Walk-in'}</Text>
                          <Text size="small" tone="secondary">
                            {invoice.created_at ? new Date(invoice.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </Text>
                        </Box>
                      </button>
                    ))
                  ) : (
                    <div className="billing-selector-empty">
                      <Text tone="secondary">
                        {invoiceSearchTerm ? 'No matching invoices found.' : 'No invoices found.'}
                      </Text>
                    </div>
                  )}
                </div>
              </Box>
            </aside>
          )}

          <SearchItemsModal
            isOpen={searchModalOpen}
            categories={categories}
            selectedCategory={selectedCategory}
            searchTerm={searchTerm}
            results={filteredSearchResults}
            onClose={closeSearchModal}
            onCategoryChange={setSelectedCategory}
            onSearchTermChange={setSearchTerm}
            onAddItem={(item) => {
              handleAddItemFromSearch(item);
              closeSearchModal();
            }}
            onEnterKey={(item) => {
              handleAddItemFromSearch(item);
              closeSearchModal();
            }}
            hideCategories={searchModalHideCategories}
          />

          <CardPaymentModal
            isOpen={cardPaymentModalOpen}
            selectedPaymentModeLabel={selectedPaymentMode?.paytypedesc || selectedPaymode}
            selectedPaymode={selectedPaymode}
            outstandingAmount={payment.outstandingAmount}
            selectedCardPaymentConfig={selectedCardPaymentConfig}
            cardPaymentDraft={cardPaymentDraft}
            cardPaymentConfigs={cardPaymentConfigs}
            onClose={() => setCardPaymentModalOpen(false)}
            onDraftChange={handleCardDraftChange}
            onApply={handleApplyCardPayment}
          />

          <PriceChangeModal
            isOpen={priceChangeModalOpen}
            itemName={priceChangeItemName}
            itemCode={priceChangeItemCode}
            priceChanges={priceChanges}
            loading={priceChangesLoading}
            error={priceChangesError}
            onClose={closePriceChangeModal}
            onSelectPrice={handleSelectPriceFromHistory}
          />

          {receiptToPrint && (
            <ReceiptPrintModal
              receipt={receiptToPrint}
              printPaperSize={printPaperSize}
              isLoading={isReceiptPrintLoading}
              onCancel={() => { setReceiptToPrint(null); focusBarcodeInput(); }}
              onConfirm={handleConfirmReceiptPrint}
              onPaperSizeChange={setPrintPaperSize}
            />
          )}

          {quotationToPrint && (
            <QuotationPrintModal
              orderNo={quotationToPrint.orderNo}
              customerName={quotationToPrint.customerName}
              amount={quotationToPrint.amount}
              isLoading={isQuotationPrintLoading}
              onCancel={() => { setQuotationToPrint(null); focusBarcodeInput(); }}
              onConfirm={handleConfirmQuotationPrint}
            />
          )}

          <PromotionsViewModal
            isOpen={showPromotionsModal}
            promotions={currentlyValidPromotions}
            selectedPromotionId={appliedPromotions[0]?.id}
            onSelect={handlePromotionSelect}
            onClose={handlePromotionsModalClose}
          />

          {/* Return Item Selection Modal */}
          <ReturnItemSelectionModal
            isOpen={showReturnItemSelectionModal}
            invoiceNo={loadedReturnInvoice?.invoice_no}
            customerName={loadedReturnInvoice?.customer_name}
            items={invoiceItemsBeforeSelection}
            selectedItems={selectedReturnItems}
            onClose={() => {
              setShowReturnItemSelectionModal(false);
              setSelectedReturnItems(new Set());
              setInvoiceItemsBeforeSelection([]);
              setLoadedReturnInvoice(null);
            }}
            onConfirm={handleReturnItemSelectionConfirm}
            onSelectedItemsChange={setSelectedReturnItems}
          />

          <div id="print-page" className="print-only">
            <div className="print-page-content">
              <div className="print-page-header">
                <div className="print-page-timestamp">{printTimestamp}</div>
                <div className="print-page-title">Sales Invoice</div>
                <div className="print-page-bank">AB Bank</div>
              </div>

              <div className="print-page-summary-meta">
                <div>Items: {items.length}</div>
                <div>Quantity: {totalQuantity}</div>
              </div>

              <table className="print-page-items-table">
                <thead>
                  <tr>
                    <th className="print-col-no">#</th>
                    <th className="print-col-name">Name</th>
                    <th className="print-col-price">Price</th>
                    <th className="print-col-qty">Qty</th>
                    <th className="print-col-total">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((item, index) => (
                      <tr key={`print-${item.id}`}>
                        <td className="print-cell-no">{index + 1}</td>
                        <td className="print-cell-name">{item.name}</td>
                        <td className="print-cell-price">{item.price.toFixed(2)}</td>
                        <td className="print-cell-qty">{item.quantity}</td>
                        <td className="print-cell-total">{item.total.toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="print-empty-state">No items in bill</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="print-page-totals-wrap">
                <table className="print-page-totals-table">
                  <tbody>
                    <tr>
                      <td className="print-total-label">Subtotal</td>
                      <td className="print-total-value">{payment.subtotal.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="print-total-label">Bill Discount</td>
                      <td className="print-total-value">
                        {payment.billDiscount.toFixed(2)}% (-{payment.billDiscountAmount.toFixed(2)})
                      </td>
                    </tr>
                    <tr>
                      <td className="print-total-label">Payment Due</td>
                      <td className="print-total-value">{payment.totalDue.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="print-total-label">Paid Amount</td>
                      <td className="print-total-value">{payment.paymentReceived.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td className="print-total-label print-total-label-strong">Change</td>
                      <td className="print-total-value print-total-value-strong">{payment.change.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {isLoyaltyPaymentModalOpen && (
            <div className="billing-loyalty-modal-overlay" role="presentation" onClick={() => setIsLoyaltyPaymentModalOpen(false)}>
              <div
                className="billing-loyalty-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="billing-loyalty-modal-title"
                onClick={(event) => event.stopPropagation()}
              >
                <Stack space="medium">
                  <Text id="billing-loyalty-modal-title" weight="strong" size="large">
                    Redeem Loyalty Points
                  </Text>
                  <Box className="billing-loyalty-modal-summary">
                    <Text size="small" tone="secondary">
                      Current points: {selectedCustomerLoyaltyPoints.toFixed(2)}
                    </Text>
                    <Text size="small" tone="secondary">
                      1 point = 1 LKR
                    </Text>
                  </Box>
                  <label className="billing-loyalty-modal-field">
                    <span>Points to redeem</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={loyaltyPaymentDraft}
                      onChange={(event) => {
                        setLoyaltyPaymentDraft(event.target.value);
                        setLoyaltyPaymentError('');
                      }}
                      className="billing-loyalty-modal-input"
                    />
                  </label>
                  {loyaltyPaymentError ? (
                    <Text tone="critical" size="small">
                      {loyaltyPaymentError}
                    </Text>
                  ) : null}
                  <div className="billing-loyalty-modal-actions">
                    <Button variant="ghost" tone="critical" onClick={() => setIsLoyaltyPaymentModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="solid" tone="brandAccent" onClick={handleApplyLoyaltyPayment}>
                      Apply Points
                    </Button>
                  </div>
                </Stack>
              </div>
            </div>
          )}
        </>
      )}
      <CreateUpdateCustomerDrawer
        isOpen={isCustomerDrawerOpen}
        onClose={() => setIsCustomerDrawerOpen(false)}
        onSave={handleSaveCustomer}
        onCustomerSaved={handleCustomerSaved}
      />
    </Box>
  );
};

export default BillingScreen;
